import { capitalize } from "lodash";
import PATHS from "routes/paths";
import Render from "lib/Render";
import {
  ANIMATION_STAGES,
  STAGE_ORDER,
  CHARACTER_PULSE_SCALE
} from "./animationConstants";

export const ANIMATION_DURATION = 1000;
export const CHARACTER_DELAY = 40; // 40ms between characters

// Typewriter animation timing configuration
// Base delay is slower for more natural feel, with randomization for human-like variation
export const TYPEIN_BASE_DELAY = 65; // Base delay between characters (ms)
export const TYPEIN_DELAY_VARIATION = 35; // Random variation range (±ms) for natural typing
export const TYPEIN_PUNCTUATION_PAUSE = 150; // Extra pause after punctuation (ms)
export const TYPEIN_SPACE_PAUSE = 30; // Small pause after spaces (ms)

// Characters that trigger longer pauses (like human thinking/breathing)
const PUNCTUATION_CHARS = new Set([".", ",", "!", "?", ";", ":", "-"]);

// Cache for character advance width measurements (preserves kerning data)
const _charWidthCache = new Map();

/**
 * Measures the advance width of each non-space character in the original text,
 * preserving kerning information from the browser's text shaper.
 *
 * When characters are wrapped in individual inline-block spans for animation,
 * the browser shapes each character independently, losing kerning adjustments
 * between character pairs. This causes subtle width changes per line that shift
 * center/right-aligned text visually.
 *
 * By measuring the original kerned widths and setting them as explicit widths
 * on character spans, we preserve the original text layout during animation.
 */
const measureCharacterAdvanceWidths = (htmlString, elementData) => {
  const cacheKey = `${elementData.uniqueId}:${htmlString}`;
  if (_charWidthCache.has(cacheKey)) {
    return _charWidthCache.get(cacheKey);
  }

  try {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.visibility = "hidden";
    div.style.top = "-9999px";
    div.style.left = "-9999px";
    div.style.fontSize = `${Number(elementData.fontSize).toFixed(2)}px`;
    div.style.fontFamily = `"${elementData.fontFamily}"`;
    div.style.lineHeight = `${elementData.lineHeight}em`;
    div.style.letterSpacing = `${(elementData.letterSpacing || 0) / 1000}em`;
    div.style.fontWeight = elementData.bold || "normal";
    div.style.wordBreak = "break-word";
    div.style.width = `${elementData.width}px`;
    div.innerHTML = htmlString;
    document.body.appendChild(div);

    const widths = [];
    let hasValidMeasurement = false;
    const walker = document.createTreeWalker(
      div,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    let textNode;
    while ((textNode = walker.nextNode())) {
      const text = textNode.textContent;
      for (let i = 0; i < text.length; i++) {
        if (/\s/.test(text[i])) continue;
        const range = document.createRange();
        range.setStart(textNode, i);
        range.setEnd(textNode, i + 1);
        const rect = range.getBoundingClientRect();
        if (rect.width > 0) hasValidMeasurement = true;
        widths.push(rect.width);
      }
    }

    document.body.removeChild(div);

    // In test environments (JSDOM), Range.getBoundingClientRect returns zeros.
    // Return null so callers skip width compensation gracefully.
    if (!hasValidMeasurement) return null;

    _charWidthCache.set(cacheKey, widths);
    return widths;
  } catch (e) {
    return null;
  }
};

// Render compensation constant for scrubbing accuracy (~2 frames at 60fps)
// This accounts for the delay between CSS animation frame and pause calculation
export const RENDER_COMPENSATION_MS = 30;

/**
 * Gets the canvas page DOM element.
 * @param {string} pageId - The page ID to look for
 * @returns {HTMLElement|null} The canvas page DOM element or null if not found
 */
const getCanvasPageElement = pageId => {
  if (Render.isRenderMode()) {
    return document.getElementById("page");
  }
  return document.getElementById(`PAGE_CANVAS-${pageId}`);
};

/**
 * Calculates a natural, variable delay for typewriter animation
 * Uses seeded randomization based on character index for consistent replay
 * @param {number} charIndex - Index of the character
 * @param {string} char - The character being typed
 * @param {string} prevChar - The previous character (for punctuation detection)
 * @returns {number} Delay in milliseconds
 */
export const calculateTypewriterDelay = (charIndex, char, prevChar = "") => {
  // Use character index as seed for consistent "random" variation on replay
  // This creates pseudo-random but deterministic delays using a simple hash
  // function (constants 12.9898, 78.233, 43758.5453 are from a well-known
  // one-liner PRNG that produces good distribution for visual purposes)
  const seedRandom = Math.sin(charIndex * 12.9898 + 78.233) * 43758.5453;
  const randomFactor = (seedRandom - Math.floor(seedRandom)) * 2 - 1; // -1 to 1

  // Base delay with variation
  let delay = TYPEIN_BASE_DELAY + randomFactor * TYPEIN_DELAY_VARIATION;

  // Add extra pause after punctuation (simulates human thinking)
  if (PUNCTUATION_CHARS.has(prevChar)) {
    delay += TYPEIN_PUNCTUATION_PAUSE;
  }

  return Math.max(20, delay); // Minimum 20ms delay
};

/**
 * Mulberry32 seeded PRNG - produces deterministic random numbers from a seed.
 * Used for random character animation ordering to ensure consistent playback.
 * @param {number} seed - Integer seed value
 * @returns {function} Function that returns next random number between 0 and 1
 */
const mulberry32 = seed => {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * Converts a string to a numeric seed for PRNG.
 * Uses a simple hash algorithm to convert element IDs to seeds.
 * @param {string} str - String to convert (typically element ID)
 * @returns {number} Numeric seed value
 */
const stringToSeed = str => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

/**
 * Fisher-Yates shuffle with seeded random for deterministic results.
 * Shuffles array in place (modifies copy) and returns shuffled array.
 * @param {Array} array - Array to shuffle
 * @param {number} seed - Seed for deterministic shuffling
 * @returns {Array} Shuffled copy of the array
 */
const seededShuffle = (array, seed) => {
  const random = mulberry32(seed);
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/**
 * Generates shuffled delay indices for random character animation.
 * Returns an array where each index maps to its shuffled delay position.
 * @param {number} charCount - Number of characters to generate delays for
 * @param {string} elementId - Element ID to use as seed for consistent ordering
 * @returns {Array<number>} Array of shuffled delay indices
 */
export const generateRandomOrder = (charCount, elementId) => {
  if (charCount <= 0) return [];
  const indices = Array.from({ length: charCount }, (_, i) => i);
  const seed = stringToSeed(elementId);
  return seededShuffle(indices, seed);
};

// Alias for backward compatibility within this file
const getRandomCharacterDelays = generateRandomOrder;

/**
 * Calculates the character count from an HTML string, excluding whitespace.
 * Strips HTML tags and counts only non-whitespace characters.
 * Used for determining animation delay counts for letter/random animations.
 * @param {string} displayValue - HTML string (typically from element displayValue)
 * @returns {number} Character count (excluding whitespace)
 */
export const getCharacterCount = displayValue => {
  if (!displayValue) return 0;
  const textContent = displayValue.replace(/<[^>]*>/g, "");
  const words = textContent.split(/\s+/).filter(w => w.length > 0);
  return words.reduce((sum, word) => sum + word.length, 0);
};

// Random direction pool - only up/down for visual consistency
const RANDOM_DIRECTION_POOL = ["up", "down"];

// Seed offset for random directions to ensure different sequence from random order
const RANDOM_DIRECTION_SEED_OFFSET = 1000;

/**
 * Generates random directions (up/down only) for each character in a text animation.
 * Uses seeded random for deterministic results - same element always gets same directions.
 * @param {number} charCount - Number of characters to generate directions for
 * @param {string} elementId - Element ID to use as seed for consistent directions
 * @returns {Array<string>} Array of direction strings: ['up', 'down', 'up', ...]
 */
export const generateRandomDirections = (charCount, elementId) => {
  if (charCount <= 0) return [];

  // Use offset seed to generate different sequence from random order
  const seed = stringToSeed(elementId) + RANDOM_DIRECTION_SEED_OFFSET;
  const random = mulberry32(seed);

  const directions = [];
  for (let i = 0; i < charCount; i++) {
    const index = Math.floor(random() * RANDOM_DIRECTION_POOL.length);
    directions.push(RANDOM_DIRECTION_POOL[index]);
  }

  return directions;
};

/**
 * Calculates slide distance based on element and canvas bounds
 * @param {Object} elementBounds - Element bounding rectangle
 * @param {Object} canvasBounds - Canvas bounding rectangle
 * @param {string} direction - Slide direction
 * @param {number} zoom - Current zoom level
 * @returns {number} Slide distance in pixels
 */
const calculateSlideDistance = (
  elementBounds,
  canvasBounds,
  direction,
  zoom
) => {
  let distance = 0;

  if (direction === "left") {
    distance =
      (elementBounds.left - canvasBounds.left + elementBounds.width) / zoom;
  } else if (direction === "right") {
    distance =
      (canvasBounds.right - elementBounds.right + elementBounds.width) / zoom;
  } else if (direction === "up") {
    distance =
      (elementBounds.top - canvasBounds.top + elementBounds.height) / zoom;
  } else if (direction === "down") {
    distance =
      (canvasBounds.bottom - elementBounds.bottom + elementBounds.height) /
      zoom;
  }

  // Ensure we have enough distance to slide completely off screen
  return Math.max(distance, elementBounds.height / zoom);
};

/**
 * Gets CSS transform string for slide animation
 * @param {number} distance - Distance to slide
 * @param {string} direction - Slide direction (the direction the element will move)
 * @param {boolean} isSlideOut - Whether this is a slide-out animation
 * @param {number} scaleX - Horizontal flip state (1 = normal, -1 = flipped)
 * @param {number} scaleY - Vertical flip state (1 = normal, -1 = flipped)
 * @returns {string} CSS transform value
 */
const getSlideTransform = (
  distance,
  direction,
  isSlideOut = false,
  scaleX = 1,
  scaleY = 1
) => {
  // For normal position (no transform)
  const noTransform = "translateX(0) translateY(0)";

  // When an element is flipped (scaleX: -1 or scaleY: -1), the coordinate system is inverted
  // We need to compensate by inverting the translate direction to maintain visual consistency
  const flipCompensationX = scaleX === -1 ? -1 : 1;
  const flipCompensationY = scaleY === -1 ? -1 : 1;

  // Direction indicates where the element will move
  // For slide-in: Start from opposite side of target direction
  // For slide-out: Move in the target direction
  if (direction === "left") {
    const baseTransform = isSlideOut ? -distance : distance;
    return `translateX(${baseTransform * flipCompensationX}px)`; // Compensate for horizontal flip
  } else if (direction === "right") {
    const baseTransform = isSlideOut ? distance : -distance;
    return `translateX(${baseTransform * flipCompensationX}px)`; // Compensate for horizontal flip
  } else if (direction === "up") {
    const baseTransform = isSlideOut ? -distance : distance;
    return `translateY(${baseTransform * flipCompensationY}px)`; // Compensate for vertical flip
  } else if (direction === "down") {
    const baseTransform = isSlideOut ? distance : -distance;
    return `translateY(${baseTransform * flipCompensationY}px)`; // Compensate for vertical flip
  }
  return noTransform;
};

/**
 * Gets both transform and clip-path values for rise animation.
 *
 * Rise animations intentionally combine transform (to move the element) with clip-path
 * (to hide the portion outside the original bounding box). This creates a "toast rising
 * from a toaster" visual effect where the element appears to move while staying within
 * its original bounds.
 *
 * The clip-path masks any portion that would extend beyond the element's boundary,
 * ensuring the animation never visually exceeds the original bounding box.
 *
 * For rise-IN: Element enters FROM the direction (e.g., "up" means enters from below)
 * For rise-OUT: Element exits TO the direction (e.g., "up" means exits above)
 *
 * @param {string} direction - Rise direction (up, down, left, right)
 * @param {boolean} isRiseIn - true for rise-in, false for rise-out
 * @param {number} progress - Animation progress 0 to 1 (0=start, 1=end)
 * @param {number} scaleX - Horizontal flip state (1 or -1)
 * @param {number} scaleY - Vertical flip state (1 or -1)
 * @returns {Object} Object with transform and clipPath CSS properties
 */
const getRiseStyles = (
  direction,
  isRiseIn,
  progress,
  scaleX = 1,
  scaleY = 1
) => {
  // Apply flip compensation to direction
  let effectiveDirection = direction;

  if (scaleY === -1) {
    if (direction === "up") effectiveDirection = "down";
    else if (direction === "down") effectiveDirection = "up";
  }

  if (scaleX === -1) {
    if (direction === "left") effectiveDirection = "right";
    else if (direction === "right") effectiveDirection = "left";
  }

  // amount: 0 when element is at final/visible position, 100 when fully hidden
  const amount = isRiseIn ? (1 - progress) * 100 : progress * 100;

  // inset(top right bottom left) - clips from each edge
  // The element must stay within its original bounding box at all times

  switch (effectiveDirection) {
    case "up":
      if (isRiseIn) {
        // Rise IN up: element enters from BELOW, moving upward
        // - Starts below (+Y), ends at position
        // - Clip from bottom (hide part that's below original boundary)
        return {
          transform: `translateY(${amount}%)`,
          clipPath: `inset(0 0 ${amount}% 0)`
        };
      } else {
        // Rise OUT up: element exits ABOVE, moving upward
        // - Starts at position, ends above (-Y)
        // - Clip from top (hide part that's above original boundary)
        return {
          transform: `translateY(-${amount}%)`,
          clipPath: `inset(${amount}% 0 0 0)`
        };
      }
    case "down":
      if (isRiseIn) {
        // Rise IN down: element enters from ABOVE, moving downward
        // - Starts above (-Y), ends at position
        // - Clip from top (hide part that's above original boundary)
        return {
          transform: `translateY(-${amount}%)`,
          clipPath: `inset(${amount}% 0 0 0)`
        };
      } else {
        // Rise OUT down: element exits BELOW, moving downward
        // - Starts at position, ends below (+Y)
        // - Clip from bottom (hide part that's below original boundary)
        return {
          transform: `translateY(${amount}%)`,
          clipPath: `inset(0 0 ${amount}% 0)`
        };
      }
    case "left":
      if (isRiseIn) {
        // Rise IN left: element enters from the RIGHT, moving leftward
        // - Starts right (+X), ends at position
        // - Clip from right (hide part that's right of original boundary)
        return {
          transform: `translateX(${amount}%)`,
          clipPath: `inset(0 ${amount}% 0 0)`
        };
      } else {
        // Rise OUT left: element exits to the LEFT, moving leftward
        // - Starts at position, ends left (-X)
        // - Clip from left (hide part that's left of original boundary)
        return {
          transform: `translateX(-${amount}%)`,
          clipPath: `inset(0 0 0 ${amount}%)`
        };
      }
    case "right":
      if (isRiseIn) {
        // Rise IN right: element enters from the LEFT, moving rightward
        // - Starts left (-X), ends at position
        // - Clip from left (hide part that's left of original boundary)
        return {
          transform: `translateX(-${amount}%)`,
          clipPath: `inset(0 0 0 ${amount}%)`
        };
      } else {
        // Rise OUT right: element exits to the RIGHT, moving rightward
        // - Starts at position, ends right (+X)
        // - Clip from right (hide part that's right of original boundary)
        return {
          transform: `translateX(${amount}%)`,
          clipPath: `inset(0 ${amount}% 0 0)`
        };
      }
    default:
      return {
        transform: "translateY(0)",
        clipPath: "inset(0 0 0 0)"
      };
  }
};

/**
 * Gets the initial rise animation styles (at animation start).
 * For rise-in: Returns the hidden state styles
 * For rise-out: Returns the visible state styles (animation will move to hidden)
 *
 * @param {string} direction - Rise direction (up, down, left, right)
 * @param {boolean} isRiseIn - true for rise-in, false for rise-out
 * @param {number} scaleX - Horizontal flip state (1 or -1)
 * @param {number} scaleY - Vertical flip state (1 or -1)
 * @returns {Object} Object with transform and clipPath CSS values
 */
const getInitialRiseStyles = (direction, isRiseIn, scaleX = 1, scaleY = 1) => {
  if (isRiseIn) {
    return getRiseStyles(direction, isRiseIn, 0, scaleX, scaleY);
  }
  // For rise-out, start at visible position
  const isHorizontal = direction === "left" || direction === "right";
  return {
    transform: isHorizontal ? "translateX(0)" : "translateY(0)",
    clipPath: "inset(0 0 0 0)"
  };
};

/**
 * Applies initial rise animation styles to an element.
 * For rise-in: Sets the starting position (hidden state)
 * For rise-out: Sets the visible state (animation will move to hidden)
 *
 * @param {HTMLElement} element - The DOM element to apply styles to
 * @param {string} direction - Rise direction (up, down, left, right)
 * @param {boolean} isRiseIn - true for rise-in, false for rise-out
 * @param {number} scaleX - Horizontal flip state (1 or -1)
 * @param {number} scaleY - Vertical flip state (1 or -1)
 */
const applyInitialRiseStyles = (
  element,
  direction,
  isRiseIn,
  scaleX,
  scaleY
) => {
  const styles = getInitialRiseStyles(direction, isRiseIn, scaleX, scaleY);
  element.style.transform = styles.transform;
  element.style.clipPath = styles.clipPath;
};

/**
 * Builds CSS selectors for cleaning up old animation style tags.
 * Used to remove stale style tags when direction or transition changes.
 *
 * @param {string} prefix - The style ID prefix (e.g., "char-rise-animation", "custom-animation-customrise")
 * @param {string} currentDirection - Current animation direction
 * @param {string} currentTransition - Current animation transition (in/out)
 * @param {string} animationId - The unique animation/element ID
 * @returns {string[]} Array of CSS selectors for old style tags to remove
 */
const buildCleanupSelectors = (
  prefix,
  currentDirection,
  currentTransition,
  animationId
) => {
  const selectors = [];
  ["up", "down", "left", "right"].forEach(dir => {
    ["in", "out"].forEach(trans => {
      if (dir !== currentDirection || trans !== currentTransition) {
        selectors.push(`#${prefix}-${dir}-${trans}-${animationId}`);
      }
    });
  });
  return selectors;
};

/**
 * Removes old animation style tags that don't match current direction/transition.
 *
 * @param {string} prefix - The style ID prefix
 * @param {string} currentDirection - Current animation direction
 * @param {string} currentTransition - Current animation transition
 * @param {string} animationId - The unique animation/element ID
 */
const cleanupOldAnimationStyles = (
  prefix,
  currentDirection,
  currentTransition,
  animationId
) => {
  const selectors = buildCleanupSelectors(
    prefix,
    currentDirection,
    currentTransition,
    animationId
  );
  if (selectors.length > 0) {
    document.querySelectorAll(selectors.join(", ")).forEach(el => el.remove());
  }
};

/**
 * Removes old custom animation style tags (for slide/rise element animations).
 * Uses the pattern: custom-animation-custom{name}-{dir}-{trans}-{elementId}
 *
 * @param {string} animationName - The animation name (e.g., "slide", "rise")
 * @param {string} currentDirection - Current animation direction
 * @param {string} currentTransition - Current animation transition
 * @param {string} elementId - The unique element ID
 */
const cleanupOldCustomAnimationStyles = (
  animationName,
  currentDirection,
  currentTransition,
  elementId
) => {
  const selectors = [];
  ["up", "down", "left", "right", "random"].forEach(dir => {
    ["in", "out"].forEach(trans => {
      if (dir !== currentDirection || trans !== currentTransition) {
        const oldAnimName = `custom${animationName}-${dir}-${trans}-${elementId}`;
        selectors.push(`#custom-animation-${oldAnimName}`);
      }
    });
  });
  if (selectors.length > 0) {
    document.querySelectorAll(selectors.join(", ")).forEach(el => el.remove());
  }
};

/**
 * Removes old character slide animation style tags that don't match current
 * direction, transition, or textType. This ensures that switching between
 * directions or textTypes properly regenerates CSS with the correct settings.
 *
 * @param {string} currentDirection - Current animation direction
 * @param {string} currentTransition - Current animation transition
 * @param {string} currentTextType - Current text type ("letter" or "random")
 * @param {string} animationId - The unique animation/element ID
 */
const cleanupOldSlideAnimationStyles = (
  currentDirection,
  currentTransition,
  currentTextType,
  animationId
) => {
  const directions = ["up", "down", "left", "right", "random"];
  const transitions = ["in", "out"];
  const textTypes = ["letter", "random"];

  directions.forEach(dir => {
    transitions.forEach(trans => {
      textTypes.forEach(textType => {
        if (
          dir !== currentDirection ||
          trans !== currentTransition ||
          textType !== currentTextType
        ) {
          const oldStyleId = `char-slide-animation-${dir}-${trans}-${textType}-${animationId}`;
          const existingStyle = document.getElementById(oldStyleId);
          if (existingStyle) {
            existingStyle.remove();
          }
        }
      });
    });
  });
};

/**
 * Removes old character rise animation style tags that don't match current
 * direction, transition, or textType. This ensures that switching between
 * directions or textTypes properly regenerates CSS with the correct settings.
 *
 * @param {string} currentDirection - Current animation direction
 * @param {string} currentTransition - Current animation transition
 * @param {string} currentTextType - Current text type ("letter" or "random")
 * @param {string} animationId - The unique animation/element ID
 */
const cleanupOldRiseAnimationStyles = (
  currentDirection,
  currentTransition,
  currentTextType,
  animationId
) => {
  const directions = ["up", "down", "left", "right", "random"];
  const transitions = ["in", "out"];
  const textTypes = ["letter", "random"];

  directions.forEach(dir => {
    transitions.forEach(trans => {
      textTypes.forEach(textType => {
        if (
          dir !== currentDirection ||
          trans !== currentTransition ||
          textType !== currentTextType
        ) {
          const oldStyleId = `char-rise-animation-${dir}-${trans}-${textType}-${animationId}`;
          const existingStyle = document.getElementById(oldStyleId);
          if (existingStyle) {
            existingStyle.remove();
          }
        }
      });
    });
  });
};

/**
 * Removes old character pulse animation style tags that don't match current textType.
 * Pulse is a singleTransitionPreset (no direction/transition), so we only vary by textType.
 *
 * @param {string} currentTextType - Current text type ("letter" or "random")
 * @param {string} animationId - The unique animation/element ID
 */
const cleanupOldPulseAnimationStyles = (currentTextType, animationId) => {
  const textTypes = ["letter", "random"];

  textTypes.forEach(textType => {
    if (textType !== currentTextType) {
      const oldStyleId = `char-pulse-animation-${textType}-${animationId}`;
      const existingStyle = document.getElementById(oldStyleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    }
  });
};

/**
 * Helper function to sort animations by stage order
 * @param {Object} a - First animation
 * @param {Object} b - Second animation
 * @returns {number} Sort order
 */
const sortByStageOrder = (a, b) => {
  const aIndex = STAGE_ORDER.indexOf(a.stage);
  const bIndex = STAGE_ORDER.indexOf(b.stage);
  return aIndex - bIndex;
};

/**
 * Gets the status of an "animate out" (end stage) animation
 * @param {Array} cssAnimations - Array of stage animations
 * @param {number} currentPlayingTime - Current time in milliseconds
 * @param {Object} elementData - Element data for duration calculations
 * @returns {string} 'waiting', 'active', 'completed', or 'none'
 */
const getAnimateOutStatus = (
  cssAnimations,
  currentPlayingTime,
  elementData
) => {
  if (!cssAnimations || cssAnimations.length === 0) {
    return "none";
  }

  // Find the "end" stage animation with transition "out"
  const endAnimation = cssAnimations.find(
    anim => anim.stage === ANIMATION_STAGES.END && anim.transition === "out"
  );

  if (!endAnimation) {
    return "none"; // No animate out animation
  }

  // Calculate animation timing
  const animationStartTime = endAnimation.delay || 0;
  const animationDuration = calculateSingleAnimationDuration(
    endAnimation,
    elementData
  );
  const animationEndTime = animationStartTime + animationDuration;

  if (currentPlayingTime < animationStartTime) {
    return "waiting"; // Animation hasn't started yet
  } else if (
    currentPlayingTime >= animationStartTime &&
    currentPlayingTime <= animationEndTime
  ) {
    return "active"; // Animation is currently playing
  } else {
    return "completed"; // Animation has finished
  }
};

/**
 * Determines which animation should be active at a given time
 * @param {Array} cssAnimations - Array of stage animations
 * @param {number} currentPlayingTime - Current time in milliseconds
 * @param {Object} elementData - Element data for duration calculations
 * @returns {Object|null} Active animation or null
 */
export const getActiveAnimationAtTime = (
  cssAnimations,
  currentPlayingTime,
  elementData
) => {
  if (!cssAnimations || cssAnimations.length === 0) {
    return null;
  }

  // First, check for any preview animations - they take priority over time-based animations
  const previewAnimation = cssAnimations.find(anim => anim.isPreview);
  if (previewAnimation) {
    return {
      ...previewAnimation,
      relativeTime: 0, // Preview animations start from the beginning
      isActive: true
    };
  }

  // Sort animations by stage order
  const sortedAnimations = [...cssAnimations].sort(sortByStageOrder);

  for (const animation of sortedAnimations) {
    // Use absolute delay from timeline start, not cumulative
    const animationStartTime = animation.delay || 0;
    const animationDuration = calculateSingleAnimationDuration(
      animation,
      elementData
    );
    const animationEndTime = animationStartTime + animationDuration;

    if (
      currentPlayingTime >= animationStartTime &&
      currentPlayingTime <= animationEndTime
    ) {
      const activeAnimation = {
        ...animation,
        relativeTime: currentPlayingTime - animationStartTime,
        isActive: true
      };

      return activeAnimation;
    }
  }

  return null;
};

/**
 * Processes a single animation (either from array or legacy object)
 * @param {Object} animation - Animation configuration
 * @param {Object} elementData - Element data
 * @param {number} pageId - Page ID
 * @param {number} zoom - Zoom level
 * @param {number} relativeTime - Time relative to animation start (for timing adjustments)
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {Object} Animation styles
 */
const processSingleAnimation = (
  animation,
  elementData,
  pageId,
  zoom,
  relativeTime = 0,
  isPaused = false,
  currentPlayingTime = 0
) => {
  // Create temporary elementData with the single animation for existing functions
  const tempElementData = {
    ...elementData,
    cssAnimation: animation
  };

  // Check for text-level animations (character, word, and random)
  if (
    animation.textType === "letter" ||
    animation.textType === "word" ||
    animation.textType === "random"
  ) {
    // For multiple animations, we need to override the delay to 0 since timing is handled by getActiveAnimationAtTime
    const adjustedElementData = {
      ...tempElementData,
      cssAnimation: {
        ...animation,
        delay: 0,
        originalDelay: animation.delay
      }
    };
    return createCharacterAnimationStyles(
      adjustedElementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  const animationName = getAnimateCssClass(
    animation.name,
    animation.direction,
    animation.transition,
    false,
    scaleX,
    scaleY
  );

  const animationStyles = {
    // When paused: use negative delay to "seek" to exact frame position
    // When playing: use 0ms delay to let CSS animation run naturally from start
    // The animation stage system (getActiveAnimationAtTime) handles which animation plays when
    animationDelay: isPaused ? `-${relativeTime}ms` : "0ms",
    animationDuration: `${ANIMATION_DURATION}ms`,
    animationName: animationName,
    animationIterationCount: 1,
    animationFillMode: "both",
    // Pause the CSS animation when timeline is paused
    animationPlayState: isPaused ? "paused" : "running"
  };

  // For slide animations, we need to modify how they're applied
  if (animation.name === "slide") {
    return createSlideAnimation(
      tempElementData,
      pageId,
      zoom,
      isPaused,
      relativeTime
    );
  }

  // For rise animations, use clip-path based animation
  if (animation.name === "rise") {
    return createRiseAnimation(
      tempElementData,
      pageId,
      zoom,
      isPaused,
      relativeTime
    );
  }

  if (PATHS.isRenderer(window.location.pathname)) {
    animationStyles.animationPlayState = "paused";
  }

  return animationStyles;
};

/**
 * Processes character-level slide animations with contextual distances
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused (for Phase 4.3)
 * @param {number} currentPlayingTime - Current timeline time in milliseconds (for Phase 4.3)
 * @returns {string} Processed HTML with character spans
 */
const processCharacterSlideAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0,
  charWidths = null
) => {
  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview,
    textType,
    randomOrder,
    randomDirections
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  // Calculate slide distance using same logic as whole element slides
  const CANVAS_PAGE_DOM = getCanvasPageElement(pageId);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };

  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  // For random direction, we'll calculate per-character distances later
  // For fixed direction, calculate once here
  const isRandomDirection = direction === "random";
  const slideDistance = isRandomDirection
    ? 0 // Will be calculated per-character
    : calculateSlideDistance(
        ELEMENT_BOUNDS,
        CANVAS_PAGE_BOUNDS,
        direction,
        zoom
      );

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // Count characters for random order/direction calculations
  const textContent = htmlString.replace(/<[^>]*>/g, "");
  const parsedWords = textContent.split(/\s+/).filter(w => w.length > 0);
  const totalChars = parsedWords.reduce((sum, word) => sum + word.length, 0);

  // For random animations, use persisted randomOrder if available, otherwise compute from seed
  let randomDelayIndices = null;
  if (textType === "random") {
    // Use persisted randomOrder if available and length matches, otherwise fall back to seed-based generation
    if (randomOrder && randomOrder.length === totalChars) {
      randomDelayIndices = randomOrder;
    } else {
      randomDelayIndices = getRandomCharacterDelays(
        totalChars,
        elementData.uniqueId
      );
    }
  }

  // For random direction, use persisted randomDirections if available, otherwise compute from seed
  let perCharacterDirections = null;
  if (isRandomDirection) {
    if (randomDirections && randomDirections.length === totalChars) {
      perCharacterDirections = randomDirections;
    } else {
      perCharacterDirections = generateRandomDirections(
        totalChars,
        elementData.uniqueId
      );
    }
  }

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          // This is whitespace - preserve it exactly as is
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.style.display = "inline";
        wordWrapper.style.whiteSpace = "pre";
        wordWrapper.style.letterSpacing = "0";

        // Process each character in the word
        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          // Create unique class for this character
          const charClass = `char-slide-${animationId}-${characterIndex}`;
          span.className = charClass;

          // Apply inherited styles first
          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          // Get per-character direction for random direction mode
          const charDirection = isRandomDirection
            ? perCharacterDirections[characterIndex]
            : direction;

          // Calculate slide distance for this character's direction
          const charSlideDistance = isRandomDirection
            ? calculateSlideDistance(
                ELEMENT_BOUNDS,
                CANVAS_PAGE_BOUNDS,
                charDirection,
                zoom
              )
            : slideDistance;

          // Set initial styles for slide animation
          const existingStyle = span.getAttribute("style") || "";
          const initialStyles = [
            "display: inline-block",
            "position: relative", // Enable z-index stacking
            "vertical-align: baseline", // Maintain text baseline
            "white-space: pre", // Preserve whitespace
            "letter-spacing: 0"
          ];

          // Set initial transform based on transition type
          if (transition === "in") {
            const initialTransform = getSlideTransform(
              charSlideDistance,
              charDirection,
              false,
              scaleX,
              scaleY
            );
            initialStyles.push(`transform: ${initialTransform}`);
            initialStyles.push("opacity: 0");
          } else {
            initialStyles.push("transform: translateX(0) translateY(0)");
            initialStyles.push("opacity: 1");
          }

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            initialStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          // Set explicit width to preserve original kerned advance width
          if (charWidths && charWidths[characterIndex] !== undefined) {
            span.style.width = `${charWidths[characterIndex]}px`;
          }

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      // Replace text node with character spans
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Preserve original element's layout properties
      const computedStyle = window.getComputedStyle(node);
      const layoutProperties = [
        "display",
        "position",
        "float",
        "clear",
        "margin",
        "padding",
        "width",
        "height",
        "text-align",
        "vertical-align",
        "line-height",
        "white-space",
        "word-spacing",
        "letter-spacing",
        "text-indent"
      ];

      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      // Add computed layout properties that aren't already in inline styles
      layoutProperties.forEach(prop => {
        if (!elementStyle.includes(prop) && computedStyle[prop] !== "") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${
            computedStyle[prop]
          }`;
        }
      });

      if (elementStyle) {
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
        node.setAttribute("style", elementStyle);
      }

      // Process child nodes with inherited styles
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);

  // Generate CSS animations for character slides
  // Include direction, transition, and textType in styleId so changes trigger new style tag
  const styleId = `char-slide-animation-${direction}-${transition}-${textType}-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // Clean up old slide animation styles with different directions/transitions/textTypes
  // This ensures switching between directions or textTypes regenerates CSS correctly
  cleanupOldSlideAnimationStyles(direction, transition, textType, animationId);

  // Check if existing style has same character count - if content changed, we need to regenerate
  const existingCharCount = existingStyle?.dataset.charCount;
  const charCountMatches = existingCharCount === String(characterIndex);

  // Check if existing CSS has the correct animation play state
  // If CSS was generated while paused but we're now playing, regenerate
  const existingPlayStateMatches =
    existingStyle?.dataset.playState === (isPaused ? "paused" : "running");

  // When playing (not paused), reuse existing CSS to prevent animation restarts
  // Only recreate CSS when paused (for scrubbing), preview, first creation,
  // content changed, or play state changed
  if (
    existingStyle &&
    !isPaused &&
    !isPreview &&
    charCountMatches &&
    existingPlayStateMatches
  ) {
    return tempDiv.innerHTML;
  }

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  // Create keyframes and rules for each character
  for (let i = 0; i < characterIndex; i++) {
    const charClass = `.char-slide-${animationId}-${i}`;
    // For random animations, use shuffled delay indices
    const delayIndex =
      textType === "random" && randomDelayIndices ? randomDelayIndices[i] : i;
    const charDelay = isPreview
      ? delayIndex * CHARACTER_DELAY
      : (delay || 0) + delayIndex * CHARACTER_DELAY;

    // Get per-character direction for random direction mode
    const charDirection = isRandomDirection
      ? perCharacterDirections[i]
      : direction;

    // Calculate slide distance for this character's direction
    const charSlideDistance = isRandomDirection
      ? calculateSlideDistance(
          ELEMENT_BOUNDS,
          CANVAS_PAGE_BOUNDS,
          charDirection,
          zoom
        )
      : slideDistance;

    const keyframeName = `charSlide${charDirection}${transition}-${animationId}-${i}`;

    // Create character-specific keyframe
    if (transition === "in") {
      const fromTransform = getSlideTransform(
        charSlideDistance,
        charDirection,
        false,
        scaleX,
        scaleY
      );
      const toTransform = "translateX(0) translateY(0)";

      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${fromTransform};
            opacity: 0;
          }
          to {
            transform: ${toTransform};
            opacity: 1;
          }
        }
      `;
    } else {
      const fromTransform = "translateX(0) translateY(0)";
      const toTransform = getSlideTransform(
        charSlideDistance,
        charDirection,
        true,
        scaleX,
        scaleY
      );

      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${fromTransform};
            opacity: 1;
          }
          to {
            transform: ${toTransform};
            opacity: 0;
          }
        }
      `;
    }

    // Calculate animation delay and play-state based on pause/scrub state
    // Preview mode should always play, regardless of timeline pause state
    let animationDelay;
    let animationPlayState = "";
    if (isPaused && !isPreview) {
      // When paused, calculate how far into this character's animation we are
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        // Character hasn't started yet - show initial state
        animationDelay = "0ms";
        animationPlayState = "paused";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Character animation has finished - show final state with compensation
        const compensatedDelay = -(ANIMATION_DURATION - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        // Character is mid-animation - scrub to exact position with compensation
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${compensatedTime}ms`;
        animationPlayState = "paused";
      }
    } else {
      // When playing or previewing
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        // Character hasn't started yet - use original delay
        animationDelay = `${charDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Character animation has finished - show final state
        animationDelay = `-${ANIMATION_DURATION}ms`;
        animationPlayState = "paused";
      } else {
        // Character is mid-animation - position it correctly and keep playing
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    // Apply animation to character with proper timing function
    cssRules += `
      ${charClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay} both ${animationPlayState};
        will-change: transform, opacity;
      }
    `;
  }

  styleTag.textContent = cssRules;
  // Store character count and play state to detect changes
  styleTag.dataset.charCount = String(characterIndex);
  styleTag.dataset.playState = isPaused ? "paused" : "running";
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Processes character-level typewriter (typeIn) animations
 * Creates a typewriter effect where each character fades in sequentially
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data
 * @param {number} pageId - Page ID
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with character spans
 */
const processCharacterTypeInAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0,
  charWidths = null
) => {
  const { delay, originalDelay, isPreview } = animationConfig;

  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // First pass: collect all text content for delay calculation
  let allTextContent = "";

  const collectCharacters = node => {
    if (node.nodeType === Node.TEXT_NODE) {
      allTextContent += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName === "BR") {
        allTextContent += "\n"; // Track line breaks for context
        return;
      }
      Array.from(node.childNodes).forEach(collectCharacters);
    }
  };
  collectCharacters(tempDiv);

  // Calculate cumulative delays with natural variation
  let cumulativeDelay = 0;
  const characterDelays = [];
  for (let i = 0; i < allTextContent.length; i++) {
    const char = allTextContent[i];
    const prevChar = i > 0 ? allTextContent[i - 1] : "";

    // Skip whitespace and newlines for animation (they don't get animated)
    if (/\s/.test(char)) {
      characterDelays.push(null); // Marker for skipped chars
      // Still add delay after spaces for natural pacing
      if (char === " ") {
        cumulativeDelay += TYPEIN_SPACE_PAUSE;
      }
      continue;
    }

    characterDelays.push(cumulativeDelay);

    // Calculate delay for next character
    const charDelay = calculateTypewriterDelay(i, char, prevChar);
    cumulativeDelay += charDelay;
  }

  // Second pass: wrap characters in animated spans
  let characterIndex = 0;
  let textCharIndex = 0; // Index into allTextContent for delay lookup

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/);

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          // Advance text index for whitespace
          textCharIndex += word.length;
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.style.display = "inline";
        wordWrapper.style.whiteSpace = "pre";
        wordWrapper.style.letterSpacing = "0";

        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          const charClass = `char-typein-${animationId}-${characterIndex}`;
          span.className = charClass;

          // Store the delay for this character as data attribute
          const charCumulativeDelay = characterDelays[textCharIndex] || 0;
          span.setAttribute("data-delay", charCumulativeDelay);

          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          const existingStyle = span.getAttribute("style") || "";

          // Initial state: invisible (opacity 0)
          const initialStyles = [
            "display: inline-block",
            "vertical-align: baseline",
            "opacity: 0",
            "letter-spacing: 0"
          ];

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            initialStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          if (charWidths && charWidths[characterIndex] !== undefined) {
            span.style.width = `${charWidths[characterIndex]}px`;
          }

          wordWrapper.appendChild(span);
          characterIndex++;
          textCharIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Special handling for BR tags - preserve them exactly as is
      if (node.tagName === "BR") {
        textCharIndex++; // Account for newline in text content
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const layoutProperties = [
        "display",
        "position",
        "float",
        "clear",
        "margin",
        "padding",
        "width",
        "height",
        "text-align",
        "vertical-align",
        "line-height",
        "white-space",
        "word-spacing",
        "letter-spacing",
        "text-indent"
      ];

      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      layoutProperties.forEach(prop => {
        if (!elementStyle.includes(prop) && computedStyle[prop] !== "") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${
            computedStyle[prop]
          }`;
        }
      });

      if (elementStyle) {
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
        node.setAttribute("style", elementStyle);
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);

  // Create CSS keyframes for typewriter fade-in animation
  const styleId = `char-typein-animation-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // When playing (not paused), reuse existing CSS to prevent animation restarts
  // HOWEVER: If we just transitioned from paused to playing, we need to regenerate
  // the CSS to update animation-play-state from "paused" to "running"
  if (existingStyle && !isPaused) {
    // Check if the existing style was generated during timeline pause state
    // We use a marker comment to distinguish from CSS generated during play
    // (play-generated CSS may have some chars with paused state for finished animations)
    const wasGeneratedDuringPause = existingStyle.textContent.includes(
      "/* timeline-paused */"
    );
    if (wasGeneratedDuringPause) {
      existingStyle.remove();
      existingStyle = null;
    } else {
      return tempDiv.innerHTML;
    }
  }

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  // Add marker to identify CSS generated during timeline pause state
  // This allows us to distinguish from CSS generated during play (which may have some paused chars)
  if (isPaused && !isPreview) {
    cssRules += "/* timeline-paused */\n";
  }

  // Create keyframes for typewriter with instant visibility
  const keyframeName = `typeIn-${animationId}`;
  cssRules += `
    @keyframes ${keyframeName} {
      0% {
        opacity: 0;
      }
      1% {
        opacity: 1;
      }
      100% {
        opacity: 1;
      }
    }
  `;

  // Create rules for each character with natural, variable delays
  const charSpans = tempDiv.querySelectorAll(`[class^="char-typein-"]`);
  charSpans.forEach((span, i) => {
    const charClass = `.char-typein-${animationId}-${i}`;
    const storedDelay = parseFloat(span.getAttribute("data-delay")) || 0;

    // Calculate the actual delay including the base animation delay
    const charDelay = isPreview ? storedDelay : (delay || 0) + storedDelay;

    // Calculate animation delay and play-state based on pause/scrub state
    let animationDelay;
    let animationPlayState = "";
    const typeInDuration = 150; // Quick punch effect duration

    if (isPaused && !isPreview) {
      // When paused, calculate how far into this character's animation we are
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        // Character hasn't started yet - skip animation, let inline opacity: 0 keep it hidden
        animationDelay = null;
        animationPlayState = null;
      } else if (relativeTime >= typeInDuration) {
        // Character animation has finished - show final state (visible) with compensation
        const compensatedDelay = -(typeInDuration - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        // Character is mid-animation - scrub to exact position with compensation
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${Math.min(compensatedTime, typeInDuration)}ms`;
        animationPlayState = "paused";
      }
    } else {
      // When playing or previewing
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        // Character hasn't started yet - calculate REMAINING delay until start
        const remainingDelay = -relativeTime;
        animationDelay = `${remainingDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= typeInDuration) {
        // Character animation has finished - show final state
        animationDelay = `-${typeInDuration}ms`;
        animationPlayState = "paused";
      } else {
        // Character is mid-animation - position it correctly and keep playing
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    // Skip chars that haven't started yet when paused - let inline opacity: 0 keep them hidden
    if (animationDelay === null) {
      return;
    }

    cssRules += `
      ${charClass} {
        animation: ${keyframeName} ${typeInDuration}ms linear ${animationDelay} both ${animationPlayState};
        display: inline-block;
        vertical-align: baseline;
        will-change: opacity;
      }
    `;
  });

  styleTag.textContent = cssRules;
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Processes character-level rise animations with clip-path
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data
 * @param {number} pageId - Page ID
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with character spans
 */
const processCharacterRiseAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0,
  charWidths = null
) => {
  const { name } = animationConfig;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  if (name === "slide") {
    return processCharacterSlideAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  if (name === "typeIn") {
    return processCharacterTypeInAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview,
    textType,
    randomOrder,
    randomDirections
  } = animationConfig;

  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  const isRiseIn = transition === "in";
  const isRandomDirection = direction === "random";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // Count characters for random order/direction calculations
  const textContent = htmlString.replace(/<[^>]*>/g, "");
  const parsedWords = textContent.split(/\s+/).filter(w => w.length > 0);
  const totalChars = parsedWords.reduce((sum, word) => sum + word.length, 0);

  // For random animations, use persisted randomOrder if available, otherwise compute from seed
  let randomDelayIndices = null;
  if (textType === "random") {
    // Use persisted randomOrder if available and length matches, otherwise fall back to seed-based generation
    if (randomOrder && randomOrder.length === totalChars) {
      randomDelayIndices = randomOrder;
    } else {
      randomDelayIndices = getRandomCharacterDelays(
        totalChars,
        elementData.uniqueId
      );
    }
  }

  // For random direction, use persisted randomDirections if available, otherwise compute from seed
  let perCharacterDirections = null;
  if (isRandomDirection) {
    if (randomDirections && randomDirections.length === totalChars) {
      perCharacterDirections = randomDirections;
    } else {
      perCharacterDirections = generateRandomDirections(
        totalChars,
        elementData.uniqueId
      );
    }
  }

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/);

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.style.display = "inline";
        wordWrapper.style.whiteSpace = "nowrap";
        wordWrapper.style.letterSpacing = "0";

        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          const charClass = `char-rise-${animationId}-${characterIndex}`;
          span.className = charClass;

          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          // Get per-character direction for random direction mode
          const charDirection = isRandomDirection
            ? perCharacterDirections[characterIndex]
            : direction;

          const existingStyle = span.getAttribute("style") || "";
          const riseStyles = getInitialRiseStyles(
            charDirection,
            isRiseIn,
            scaleX,
            scaleY
          );

          const initialStyles = [
            "display: inline-block",
            "position: relative",
            "vertical-align: baseline",
            "white-space: nowrap",
            `transform: ${riseStyles.transform}`,
            `clip-path: ${riseStyles.clipPath}`,
            "letter-spacing: 0"
          ];

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            initialStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          if (charWidths && charWidths[characterIndex] !== undefined) {
            span.style.width = `${charWidths[characterIndex]}px`;
          }

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const computedStyle = window.getComputedStyle(node);
      const layoutProperties = [
        "display",
        "position",
        "float",
        "clear",
        "margin",
        "padding",
        "width",
        "height",
        "text-align",
        "vertical-align",
        "line-height",
        "white-space",
        "word-spacing",
        "letter-spacing",
        "text-indent"
      ];

      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      layoutProperties.forEach(prop => {
        if (!elementStyle.includes(prop) && computedStyle[prop] !== "") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${
            computedStyle[prop]
          }`;
        }
      });

      if (elementStyle) {
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
        node.setAttribute("style", elementStyle);
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);

  // Include direction, transition, and textType in styleId so changes trigger new style tag
  const styleId = `char-rise-animation-${direction}-${transition}-${textType}-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // Check if existing style has same character count - if content changed, we need to regenerate
  const existingCharCount = existingStyle?.dataset.charCount;
  const charCountMatches = existingCharCount === String(characterIndex);

  // Check if existing CSS has the correct animation play state
  // If CSS was generated while paused but we're now playing, regenerate
  const existingPlayStateMatches =
    existingStyle?.dataset.playState === (isPaused ? "paused" : "running");

  // When playing (not paused), reuse existing CSS to prevent animation restarts
  // Only recreate CSS when paused (for scrubbing), preview, first creation,
  // content changed, or play state changed
  if (
    existingStyle &&
    !isPaused &&
    !isPreview &&
    charCountMatches &&
    existingPlayStateMatches
  ) {
    return tempDiv.innerHTML;
  }

  // Clean up old rise animation styles with different directions/transitions/textTypes
  // This ensures switching between directions or textTypes regenerates CSS correctly
  cleanupOldRiseAnimationStyles(direction, transition, textType, animationId);

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  for (let i = 0; i < characterIndex; i++) {
    const charClass = `.char-rise-${animationId}-${i}`;
    // For random animations, use shuffled delay indices
    const delayIndex =
      textType === "random" && randomDelayIndices ? randomDelayIndices[i] : i;
    const charDelay = isPreview
      ? delayIndex * CHARACTER_DELAY
      : (delay || 0) + delayIndex * CHARACTER_DELAY;

    // Get per-character direction for random direction mode
    const charDirection = isRandomDirection
      ? perCharacterDirections[i]
      : direction;

    // Get start (progress=0) and end (progress=1) styles for this character
    // getRiseStyles handles the direction differences between rise-in and rise-out
    const startStyles = getRiseStyles(
      charDirection,
      isRiseIn,
      0,
      scaleX,
      scaleY
    );
    const endStyles = getRiseStyles(charDirection, isRiseIn, 1, scaleX, scaleY);

    const keyframeName = `charRise${charDirection}${transition}-${animationId}-${i}`;

    cssRules += `
      @keyframes ${keyframeName} {
        from {
          transform: ${startStyles.transform};
          clip-path: ${startStyles.clipPath};
        }
        to {
          transform: ${endStyles.transform};
          clip-path: ${endStyles.clipPath};
        }
      }
    `;

    let animationDelay;
    let animationPlayState = "";

    if (isPaused && !isPreview) {
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        animationDelay = "0ms";
        animationPlayState = "paused";
      } else if (relativeTime >= ANIMATION_DURATION) {
        const RENDER_COMPENSATION_MS = 30;
        const compensatedDelay = -(ANIMATION_DURATION - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        const RENDER_COMPENSATION_MS = 30;
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${compensatedTime}ms`;
        animationPlayState = "paused";
      }
    } else {
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        animationDelay = `${charDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= ANIMATION_DURATION) {
        animationDelay = `-${ANIMATION_DURATION}ms`;
        animationPlayState = "paused";
      } else {
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    cssRules += `
      ${charClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay} both ${animationPlayState};
        will-change: transform, clip-path;
      }
    `;
  }

  styleTag.textContent = cssRules;
  // Store character count and play state to detect changes
  styleTag.dataset.charCount = String(characterIndex);
  styleTag.dataset.playState = isPaused ? "paused" : "running";
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Processes character-level pulse animation with enhanced scale amplitude.
 * The default animate.css pulse uses scale3d(1.05) which is imperceptible on
 * individual characters. This generates custom keyframes with CHARACTER_PULSE_SCALE (1.15).
 *
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data
 * @param {string} pageId - Page ID
 * @param {number} zoom - Zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with character spans
 */
const processCharacterPulseAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    delay,
    originalDelay,
    isPreview,
    textType,
    randomOrder
  } = animationConfig;
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  const textContent = htmlString.replace(/<[^>]*>/g, "");
  const parsedWords = textContent.split(/\s+/).filter(w => w.length > 0);
  const totalChars = parsedWords.reduce((sum, word) => sum + word.length, 0);

  let randomDelayIndices = null;
  if (textType === "random") {
    if (randomOrder && randomOrder.length === totalChars) {
      randomDelayIndices = randomOrder;
    } else {
      randomDelayIndices = getRandomCharacterDelays(
        totalChars,
        elementData.uniqueId
      );
    }
  }

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/);

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.style.cssText = `
          display: inline-block;
          white-space: nowrap;
          vertical-align: baseline;
          word-break: break-word;
        `
          .replace(/\s+/g, " ")
          .trim();

        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          const charClass = `char-pulse-${animationId}-${characterIndex}`;
          span.className = charClass;

          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          const existingStyle = span.getAttribute("style") || "";
          const initialStyles = [
            "display: inline-block",
            "vertical-align: baseline"
          ];

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            initialStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";
      if (elementStyle) {
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
      }
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);

  const styleId = `char-pulse-animation-${textType}-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  cleanupOldPulseAnimationStyles(textType, animationId);

  const existingCharCount = existingStyle?.dataset.charCount;
  const charCountMatches = existingCharCount === String(characterIndex);

  if (existingStyle && !isPaused && charCountMatches) {
    return tempDiv.innerHTML;
  }

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  const scale = CHARACTER_PULSE_SCALE;
  let cssRules = "";

  for (let i = 0; i < characterIndex; i++) {
    const charClass = `.char-pulse-${animationId}-${i}`;
    const delayIndex =
      textType === "random" && randomDelayIndices ? randomDelayIndices[i] : i;
    const charDelay = isPreview
      ? delayIndex * CHARACTER_DELAY
      : (delay || 0) + delayIndex * CHARACTER_DELAY;

    const keyframeName = `charPulse-${animationId}-${i}`;

    cssRules += `
      @keyframes ${keyframeName} {
        from {
          transform: scale3d(1, 1, 1);
        }
        50% {
          transform: scale3d(${scale}, ${scale}, ${scale});
        }
        to {
          transform: scale3d(1, 1, 1);
        }
      }
    `;

    let animationDelay;
    let animationPlayState = "";

    if (isPaused && !isPreview) {
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        animationDelay = "0ms";
        animationPlayState = "paused";
      } else if (relativeTime >= ANIMATION_DURATION) {
        const compensatedDelay = -(ANIMATION_DURATION - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${compensatedTime}ms`;
        animationPlayState = "paused";
      }
    } else {
      const charStartTime = charDelay;
      const relativeTime = relativeTimeToAnimation - charStartTime;

      if (relativeTime < 0) {
        animationDelay = `${charDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= ANIMATION_DURATION) {
        animationDelay = `-${ANIMATION_DURATION}ms`;
        animationPlayState = "paused";
      } else {
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    cssRules += `
      ${charClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms ease-in-out ${animationDelay} both ${animationPlayState};
        will-change: transform;
      }
    `;
  }

  styleTag.textContent = cssRules;
  styleTag.dataset.charCount = String(characterIndex);
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Processes HTML string to wrap each character in animated spans
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data (for slide animations)
 * @param {number} pageId - Page ID (for slide animations)
 * @param {number} zoom - Zoom level (for slide animations)
 * @param {boolean} isPaused - Whether timeline is paused (for Phase 4.3)
 * @param {number} currentPlayingTime - Current timeline time in milliseconds (for Phase 4.3)
 * @returns {string} Processed HTML with character spans
 */
const processCharactersForAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const { name } = animationConfig;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  const charWidths = measureCharacterAdvanceWidths(htmlString, elementData);

  if (name === "slide") {
    return processCharacterSlideAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime,
      charWidths
    );
  }

  if (name === "rise") {
    return processCharacterRiseAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime,
      charWidths
    );
  }

  if (name === "typeIn") {
    return processCharacterTypeInAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime,
      charWidths
    );
  }

  if (name === "pulse") {
    return processCharacterPulseAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview,
    textType,
    randomOrder,
    randomDirections
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  const isRandomDirection = direction === "random";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // Count characters for random order/direction calculations
  const textContent = htmlString.replace(/<[^>]*>/g, "");
  const parsedWords = textContent.split(/\s+/).filter(w => w.length > 0);
  const totalChars = parsedWords.reduce((sum, word) => sum + word.length, 0);

  // For random animations, use persisted randomOrder if available, otherwise compute from seed
  let randomDelayIndices = null;
  if (textType === "random") {
    // Use persisted randomOrder if available and length matches, otherwise fall back to seed-based generation
    if (randomOrder && randomOrder.length === totalChars) {
      randomDelayIndices = randomOrder;
    } else {
      randomDelayIndices = getRandomCharacterDelays(
        totalChars,
        elementData.uniqueId
      );
    }
  }

  // For random direction, use persisted randomDirections if available, otherwise compute from seed
  let perCharacterDirections = null;
  if (isRandomDirection) {
    if (randomDirections && randomDirections.length === totalChars) {
      perCharacterDirections = randomDirections;
    } else {
      perCharacterDirections = generateRandomDirections(
        totalChars,
        elementData.uniqueId
      );
    }
  }

  let characterIndex = 0;

  const processNode = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.length === 0) return;

      const fragment = document.createDocumentFragment();
      const words = text.split(/(\s+)/); // Split by whitespace but keep the whitespace

      words.forEach(word => {
        if (!word) return;

        if (/\s+/.test(word)) {
          // This is whitespace - preserve it exactly as is
          const textNode = document.createTextNode(word);
          fragment.appendChild(textNode);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.style.display = "inline";
        wordWrapper.style.whiteSpace = "nowrap";
        wordWrapper.style.letterSpacing = "0";

        // Process each character in the word
        for (let i = 0; i < word.length; i++) {
          const char = word[i];
          const span = document.createElement("span");
          span.textContent = char;

          // For random animations, use shuffled delay indices
          // For letter animations, use sequential indices
          const delayIndex =
            textType === "random" && randomDelayIndices
              ? randomDelayIndices[characterIndex]
              : characterIndex;

          // For multiple animations, delay is 0 (timeline timing handled elsewhere)
          // For single animations or preview, use the actual delay
          const charDelay = isPreview
            ? delayIndex * CHARACTER_DELAY
            : (delay || 0) + delayIndex * CHARACTER_DELAY;

          // Get per-character direction for random direction mode
          const charDirection = isRandomDirection
            ? perCharacterDirections[characterIndex]
            : direction;

          // Create unique class for this character
          const charClass = `char-${animationId}-${characterIndex}`;
          span.className = charClass;

          // Add animate.css classes
          const animateClass = getAnimateCssClass(
            name,
            charDirection,
            transition,
            true,
            scaleX,
            scaleY
          );
          span.classList.add("animate__animated", animateClass);

          // Apply inherited styles first
          if (inheritedStyles.style) {
            span.setAttribute("style", inheritedStyles.style);
          }

          // Set animation properties via inline style (append to existing styles)
          const existingStyle = span.getAttribute("style") || "";

          // Calculate animation styles based on pause/scrub state
          // Preview mode should always play, regardless of timeline pause state
          let animationStyles;
          if (isPaused && !isPreview) {
            // When paused, calculate how far into this character's animation we are
            const charStartTime = charDelay;
            const relativeTime = relativeTimeToAnimation - charStartTime;

            if (relativeTime < 0) {
              // Character hasn't started yet - show initial state
              animationStyles = [
                `animation-delay: 0ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`,
                `letter-spacing: 0`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Character animation has finished - show final state with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedDelay = -(
                ANIMATION_DURATION - RENDER_COMPENSATION_MS
              );
              animationStyles = [
                `animation-delay: ${compensatedDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`,
                `letter-spacing: 0`
              ];
            } else {
              // Character is mid-animation - scrub to exact position with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
              animationStyles = [
                `animation-delay: -${compensatedTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`,
                `letter-spacing: 0`
              ];
            }
          } else {
            // When playing or previewing
            const charStartTime = charDelay;
            const relativeTime = relativeTimeToAnimation - charStartTime;

            if (relativeTime < 0) {
              // Character hasn't started yet - use original delay
              animationStyles = [
                `animation-delay: ${charDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `display: inline-block`,
                `vertical-align: baseline`,
                `letter-spacing: 0`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Character animation has finished - show final state
              animationStyles = [
                `animation-delay: -${ANIMATION_DURATION}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`,
                `display: inline-block`,
                `vertical-align: baseline`,
                `letter-spacing: 0`
              ];
            } else {
              // Character is mid-animation - position it correctly and keep playing
              animationStyles = [
                `animation-delay: -${relativeTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `display: inline-block`,
                `vertical-align: baseline`,
                `letter-spacing: 0`
              ];
            }
          }

          const combinedStyle =
            existingStyle +
            (existingStyle ? "; " : "") +
            animationStyles.join("; ");
          span.setAttribute("style", combinedStyle);

          if (charWidths && charWidths[characterIndex] !== undefined) {
            span.style.width = `${charWidths[characterIndex]}px`;
          }

          wordWrapper.appendChild(span);
          characterIndex++;
        }

        fragment.appendChild(wordWrapper);
      });

      // Replace text node with character spans
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Collect styles from this element to pass to children
      const currentStyles = { ...inheritedStyles };

      // Get inline styles from this element
      let elementStyle = node.getAttribute("style") || "";
      if (elementStyle) {
        // Combine inherited styles with current element styles
        const combinedStyle = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
        currentStyles.style = combinedStyle;
      }

      // Process child nodes with inherited styles
      const children = Array.from(node.childNodes);
      children.forEach(child => processNode(child, currentStyles));
    }
  };

  processNode(tempDiv);
  return tempDiv.innerHTML;
};

/**
 * Processes word-level animations with style preservation
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with word spans
 */
const processWordAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    name,
    direction,
    transition,
    delay,
    originalDelay,
    isPreview
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  // For slide animations, use custom slide processing
  if (name === "slide") {
    return processWordSlideAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  // For rise animations, use custom rise processing
  if (name === "rise") {
    return processWordRiseAnimation(
      htmlString,
      animationId,
      animationConfig,
      elementData,
      pageId,
      zoom,
      isPaused,
      currentPlayingTime
    );
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;
  const wordNodes = new Map();

  const processWordNodes = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;

      const words = text.match(/\S+|\s+/g) || [];
      words.forEach(word => {
        if (!word) return;

        if (/^\s+$/.test(word)) {
          const textNode = document.createTextNode(word);
          node.parentNode.insertBefore(textNode, node);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.className = `word-${animationId}-${wordIndex}`;
        wordWrapper.style.cssText = inheritedStyles.style || "";
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "inherit";

        // Add animate.css classes
        const animateClass = getAnimateCssClass(
          name,
          direction,
          transition,
          true,
          scaleX,
          scaleY
        );
        wordWrapper.classList.add("animate__animated", animateClass);

        // Set animation properties via inline style
        const wordDelay = isPreview
          ? wordIndex * CHARACTER_DELAY
          : delay + wordIndex * CHARACTER_DELAY;

        // Calculate animation styles based on pause/scrub state
        // Preview mode should always play, regardless of timeline pause state
        let animationStyles;
        if (isPaused && !isPreview) {
          // When paused, calculate how far into this word's animation we are
          const wordStartTime = wordDelay;
          const relativeTime = relativeTimeToAnimation - wordStartTime;

          if (relativeTime < 0) {
            // Word hasn't started yet - show initial state
            animationStyles = [
              `animation-delay: 0ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          } else if (relativeTime >= ANIMATION_DURATION) {
            // Word animation has finished - show final state with compensation
            const compensatedDelay = -(
              ANIMATION_DURATION - RENDER_COMPENSATION_MS
            );
            animationStyles = [
              `animation-delay: ${compensatedDelay}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          } else {
            // Word is mid-animation - scrub to exact position with compensation
            const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
            animationStyles = [
              `animation-delay: -${compensatedTime}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          }
        } else {
          // When playing or previewing
          const wordStartTime = wordDelay;
          const relativeTime = relativeTimeToAnimation - wordStartTime;

          if (relativeTime < 0) {
            // Word hasn't started yet - use original delay
            animationStyles = [
              `animation-delay: ${wordDelay}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`
            ];
          } else if (relativeTime >= ANIMATION_DURATION) {
            // Word animation has finished - show final state
            animationStyles = [
              `animation-delay: -${ANIMATION_DURATION}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`,
              `animation-play-state: paused`
            ];
          } else {
            // Word is mid-animation - position it correctly and keep playing
            animationStyles = [
              `animation-delay: -${relativeTime}ms`,
              `animation-duration: ${ANIMATION_DURATION}ms`,
              `animation-fill-mode: both`
            ];
          }
        }

        wordWrapper.style.cssText += `; ${animationStyles.join("; ")}`;

        const textNode = document.createTextNode(word);
        wordWrapper.appendChild(textNode);

        wordNodes.set(wordWrapper, {
          index: wordIndex,
          styles: inheritedStyles
        });

        node.parentNode.insertBefore(wordWrapper, node);
        wordIndex++;
      });

      node.parentNode.removeChild(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Special handling for BR tags - preserve them exactly as is
      if (node.tagName === "BR") {
        const br = node.cloneNode(false); // Clone without children
        node.parentNode.insertBefore(br, node);
        node.parentNode.removeChild(node);
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      const importantStyles = [
        "color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-style",
        "text-decoration-color",
        "letter-spacing",
        "line-height",
        "text-transform",
        "vertical-align"
      ];

      importantStyles.forEach(prop => {
        const value = computedStyle[prop];
        if (value && value !== "none" && value !== "normal") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${value}`;
        }
      });

      if (elementStyle) {
        currentStyles.style = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
      }

      const prevSibling = node.previousSibling;
      const nextSibling = node.nextSibling;
      const isPartOfWord =
        node.textContent.trim().length > 0 &&
        !node.textContent.includes(" ") &&
        ((prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          !prevSibling.textContent.includes(" ")) ||
          (nextSibling &&
            nextSibling.nodeType === Node.ELEMENT_NODE &&
            !nextSibling.textContent.includes(" ")));

      if (isPartOfWord) {
        let wordWrapper;
        if (
          prevSibling &&
          prevSibling.className &&
          prevSibling.className.startsWith(`word-${animationId}`)
        ) {
          wordWrapper = prevSibling;
        } else {
          wordWrapper = document.createElement("span");
          wordWrapper.className = `word-${animationId}-${wordIndex} animate__animated ${getAnimateCssClass(
            name,
            direction,
            transition,
            true,
            scaleX,
            scaleY
          )}`;
          wordWrapper.style.cssText = currentStyles.style || "";
          wordWrapper.style.display = "inline-block";
          wordWrapper.style.whiteSpace = "inherit";

          const wordDelay = isPreview
            ? wordIndex * CHARACTER_DELAY
            : delay + wordIndex * CHARACTER_DELAY;

          // Calculate animation styles based on pause/scrub state
          // Preview mode should always play, regardless of timeline pause state
          let animationStyles;
          if (isPaused && !isPreview) {
            // When paused, calculate how far into this word's animation we are
            const wordStartTime = wordDelay;
            const relativeTime = relativeTimeToAnimation - wordStartTime;

            if (relativeTime < 0) {
              // Word hasn't started yet - show initial state
              animationStyles = [
                `animation-delay: 0ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Word animation has finished - show final state with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedDelay = -(
                ANIMATION_DURATION - RENDER_COMPENSATION_MS
              );
              animationStyles = [
                `animation-delay: ${compensatedDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            } else {
              // Word is mid-animation - scrub to exact position with compensation
              const RENDER_COMPENSATION_MS = 30;
              const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
              animationStyles = [
                `animation-delay: -${compensatedTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            }
          } else {
            // When playing or previewing
            const wordStartTime = wordDelay;
            const relativeTime = relativeTimeToAnimation - wordStartTime;

            if (relativeTime < 0) {
              // Word hasn't started yet - use original delay
              animationStyles = [
                `animation-delay: ${wordDelay}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`
              ];
            } else if (relativeTime >= ANIMATION_DURATION) {
              // Word animation has finished - show final state
              animationStyles = [
                `animation-delay: -${ANIMATION_DURATION}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`,
                `animation-play-state: paused`
              ];
            } else {
              // Word is mid-animation - position it correctly and keep playing
              animationStyles = [
                `animation-delay: -${relativeTime}ms`,
                `animation-duration: ${ANIMATION_DURATION}ms`,
                `animation-fill-mode: both`
              ];
            }
          }

          wordWrapper.style.cssText += `; ${animationStyles.join("; ")}`;

          node.parentNode.insertBefore(wordWrapper, node);

          wordNodes.set(wordWrapper, {
            index: wordIndex,
            styles: currentStyles
          });
          wordIndex++;
        }

        const clonedNode = node.cloneNode(true);
        wordWrapper.appendChild(clonedNode);
        node.parentNode.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processWordNodes(child, currentStyles));

      if (node.childNodes.length === 0 && node.tagName !== "BR") {
        node.parentNode.removeChild(node);
      }
    }
  };

  processWordNodes(tempDiv);
  return tempDiv.innerHTML;
};

/**
 * Processes word-level slide animations with custom keyframes
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data for bounds calculation
 * @param {number} pageId - Page ID for canvas bounds
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with word spans
 */
const processWordSlideAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview
  } = animationConfig;
  // Calculate time relative to when this animation started on the timeline
  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  const CANVAS_PAGE_DOM = getCanvasPageElement(pageId);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };

  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;
  const wordNodes = new Map();

  const processWordNodes = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;

      const words = text.match(/\S+|\s+/g) || [];
      words.forEach(word => {
        if (!word) return;

        if (/^\s+$/.test(word)) {
          const textNode = document.createTextNode(word);
          node.parentNode.insertBefore(textNode, node);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.className = `word-${animationId}-${wordIndex}`;
        wordWrapper.style.cssText = inheritedStyles.style || "";
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "inherit";
        wordWrapper.style.position = "relative";
        wordWrapper.style.verticalAlign = "baseline";

        if (transition === "in") {
          const initialTransform = getSlideTransform(
            slideDistance,
            direction,
            false,
            scaleX,
            scaleY
          );
          wordWrapper.style.transform = initialTransform;
          wordWrapper.style.opacity = "0";
        } else if (transition === "out") {
          wordWrapper.style.transform = "translateX(0) translateY(0)";
          wordWrapper.style.opacity = "1";
        }

        const textNode = document.createTextNode(word);
        wordWrapper.appendChild(textNode);

        wordNodes.set(wordWrapper, {
          index: wordIndex,
          styles: inheritedStyles
        });

        node.parentNode.insertBefore(wordWrapper, node);
        wordIndex++;
      });

      node.parentNode.removeChild(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      // Special handling for BR tags - preserve them exactly as is
      if (node.tagName === "BR") {
        const br = node.cloneNode(false);
        node.parentNode.insertBefore(br, node);
        node.parentNode.removeChild(node);
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      const importantStyles = [
        "color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-style",
        "text-decoration-color",
        "letter-spacing",
        "line-height",
        "text-transform",
        "vertical-align"
      ];

      importantStyles.forEach(prop => {
        const value = computedStyle[prop];
        if (value && value !== "none" && value !== "normal") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${value}`;
        }
      });

      if (elementStyle) {
        currentStyles.style = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
      }

      const prevSibling = node.previousSibling;
      const nextSibling = node.nextSibling;
      const isPartOfWord =
        node.textContent.trim().length > 0 &&
        !node.textContent.includes(" ") &&
        ((prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          !prevSibling.textContent.includes(" ")) ||
          (nextSibling &&
            nextSibling.nodeType === Node.ELEMENT_NODE &&
            !nextSibling.textContent.includes(" ")));

      if (isPartOfWord) {
        let wordWrapper;
        if (
          prevSibling &&
          prevSibling.className &&
          prevSibling.className.startsWith(`word-${animationId}`)
        ) {
          wordWrapper = prevSibling;
        } else {
          wordWrapper = document.createElement("span");
          wordWrapper.className = `word-${animationId}-${wordIndex}`;
          wordWrapper.style.cssText = currentStyles.style || "";
          wordWrapper.style.display = "inline-block";
          wordWrapper.style.whiteSpace = "inherit";
          wordWrapper.style.position = "relative";
          wordWrapper.style.verticalAlign = "baseline";

          if (transition === "in") {
            const initialTransform = getSlideTransform(
              slideDistance,
              direction,
              false,
              scaleX,
              scaleY
            );
            wordWrapper.style.transform = initialTransform;
            wordWrapper.style.opacity = "0";
          } else if (transition === "out") {
            wordWrapper.style.transform = "translateX(0) translateY(0)";
            wordWrapper.style.opacity = "1";
          }

          node.parentNode.insertBefore(wordWrapper, node);

          wordNodes.set(wordWrapper, {
            index: wordIndex,
            styles: currentStyles
          });
          wordIndex++;
        }

        const clonedNode = node.cloneNode(true);
        wordWrapper.appendChild(clonedNode);
        node.parentNode.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processWordNodes(child, currentStyles));

      if (node.childNodes.length === 0 && node.tagName !== "BR") {
        node.parentNode.removeChild(node);
      }
    }
  };

  processWordNodes(tempDiv);

  const styleId = `word-animation-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // Check if existing style has same word count - if content changed, we need to regenerate
  const existingWordCount = existingStyle?.dataset.wordCount;
  const wordCountMatches = existingWordCount === String(wordNodes.size);

  // When playing (not paused), reuse existing CSS to prevent animation restarts
  // Only recreate CSS when paused (for scrubbing) or first creation or content changed
  if (existingStyle && !isPaused && wordCountMatches) {
    return tempDiv.innerHTML;
  }

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  wordNodes.forEach((data, wordWrapper) => {
    const wordClass = `.word-${animationId}-${data.index}`;
    const wordDelay = isPreview
      ? data.index * CHARACTER_DELAY
      : delay + data.index * CHARACTER_DELAY;

    const keyframeName = `wordSlide${direction}${transition}-${animationId}-${data.index}`;

    if (transition === "in") {
      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: ${getSlideTransform(
              slideDistance,
              direction,
              false,
              scaleX,
              scaleY
            )};
            opacity: 0;
          }
          to {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
        }
      `;
    } else {
      cssRules += `
        @keyframes ${keyframeName} {
          from {
            transform: translateX(0) translateY(0);
            opacity: 1;
          }
          to {
            transform: ${getSlideTransform(
              slideDistance,
              direction,
              true,
              scaleX,
              scaleY
            )};
            opacity: 0;
          }
        }
      `;
    }

    // Calculate animation delay and play-state based on pause/scrub state
    // Preview mode should always play, regardless of timeline pause state
    let animationDelay;
    let animationPlayState = "";
    if (isPaused && !isPreview) {
      // When paused, calculate how far into this word's animation we are
      const wordStartTime = wordDelay;
      const relativeTime = relativeTimeToAnimation - wordStartTime;

      if (relativeTime < 0) {
        // Word hasn't started yet - show initial state
        animationDelay = "0ms";
        animationPlayState = "paused";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Word animation has finished - show final state with compensation
        const RENDER_COMPENSATION_MS = 30;
        const compensatedDelay = -(ANIMATION_DURATION - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        // Word is mid-animation - scrub to exact position with compensation
        const RENDER_COMPENSATION_MS = 30;
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${compensatedTime}ms`;
        animationPlayState = "paused";
      }
    } else {
      // When playing or previewing
      const wordStartTime = wordDelay;
      const relativeTime = relativeTimeToAnimation - wordStartTime;

      if (relativeTime < 0) {
        // Word hasn't started yet - use original delay
        animationDelay = `${wordDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Word animation has finished - show final state
        animationDelay = `-${ANIMATION_DURATION}ms`;
        animationPlayState = "paused";
      } else {
        // Word is mid-animation - position it correctly and keep playing
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    cssRules += `
      ${wordClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay} both ${animationPlayState};
        display: inline-block;
        white-space: inherit;
        position: relative;
        z-index: ${data.index + 1};
        will-change: transform, opacity;
      }
      ${wordClass} * {
        display: inline-block;
        white-space: inherit;
      }
    `;
  });

  styleTag.textContent = cssRules;
  // Store word count to detect content changes
  styleTag.dataset.wordCount = String(wordNodes.size);
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Processes word-level rise animations with clip-path
 * @param {string} htmlString - Original HTML content
 * @param {string} animationId - Unique animation identifier
 * @param {Object} animationConfig - Animation configuration
 * @param {Object} elementData - Element data
 * @param {number} pageId - Page ID
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {string} Processed HTML with word spans
 */
const processWordRiseAnimation = (
  htmlString,
  animationId,
  animationConfig,
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    direction,
    transition,
    delay,
    originalDelay,
    isPreview
  } = animationConfig;

  const animationStartTime = originalDelay || 0;
  const relativeTimeToAnimation = currentPlayingTime - animationStartTime;

  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  const isRiseIn = transition === "in";

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  let wordIndex = 0;
  const wordNodes = new Map();

  const processWordNodes = (node, inheritedStyles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text) return;

      const words = text.match(/\S+|\s+/g) || [];
      words.forEach(word => {
        if (!word) return;

        if (/^\s+$/.test(word)) {
          const textNode = document.createTextNode(word);
          node.parentNode.insertBefore(textNode, node);
          return;
        }

        const wordWrapper = document.createElement("span");
        wordWrapper.className = `word-rise-${animationId}-${wordIndex}`;
        wordWrapper.style.cssText = inheritedStyles.style || "";
        wordWrapper.style.display = "inline-block";
        wordWrapper.style.whiteSpace = "inherit";
        wordWrapper.style.position = "relative";
        wordWrapper.style.verticalAlign = "baseline";

        applyInitialRiseStyles(
          wordWrapper,
          direction,
          isRiseIn,
          scaleX,
          scaleY
        );

        const textNode = document.createTextNode(word);
        wordWrapper.appendChild(textNode);

        wordNodes.set(wordWrapper, {
          index: wordIndex,
          styles: inheritedStyles
        });

        node.parentNode.insertBefore(wordWrapper, node);
        wordIndex++;
      });

      node.parentNode.removeChild(node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName === "BR") {
        const br = node.cloneNode(false);
        node.parentNode.insertBefore(br, node);
        node.parentNode.removeChild(node);
        return;
      }

      const computedStyle = window.getComputedStyle(node);
      const currentStyles = { ...inheritedStyles };
      let elementStyle = node.getAttribute("style") || "";

      const importantStyles = [
        "color",
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "text-decoration",
        "text-decoration-line",
        "text-decoration-style",
        "text-decoration-color",
        "letter-spacing",
        "line-height",
        "text-transform",
        "vertical-align"
      ];

      importantStyles.forEach(prop => {
        const value = computedStyle[prop];
        if (value && value !== "none" && value !== "normal") {
          elementStyle += `${elementStyle ? "; " : ""}${prop}: ${value}`;
        }
      });

      if (elementStyle) {
        currentStyles.style = inheritedStyles.style
          ? `${inheritedStyles.style}; ${elementStyle}`
          : elementStyle;
      }

      const prevSibling = node.previousSibling;
      const nextSibling = node.nextSibling;
      const isPartOfWord =
        node.textContent.trim().length > 0 &&
        !node.textContent.includes(" ") &&
        ((prevSibling &&
          prevSibling.nodeType === Node.ELEMENT_NODE &&
          !prevSibling.textContent.includes(" ")) ||
          (nextSibling &&
            nextSibling.nodeType === Node.ELEMENT_NODE &&
            !nextSibling.textContent.includes(" ")));

      if (isPartOfWord) {
        let wordWrapper;
        if (
          prevSibling &&
          prevSibling.className &&
          prevSibling.className.startsWith(`word-rise-${animationId}`)
        ) {
          wordWrapper = prevSibling;
        } else {
          wordWrapper = document.createElement("span");
          wordWrapper.className = `word-rise-${animationId}-${wordIndex}`;
          wordWrapper.style.cssText = currentStyles.style || "";
          wordWrapper.style.display = "inline-block";
          wordWrapper.style.whiteSpace = "inherit";
          wordWrapper.style.position = "relative";
          wordWrapper.style.verticalAlign = "baseline";

          applyInitialRiseStyles(
            wordWrapper,
            direction,
            isRiseIn,
            scaleX,
            scaleY
          );

          node.parentNode.insertBefore(wordWrapper, node);

          wordNodes.set(wordWrapper, {
            index: wordIndex,
            styles: currentStyles
          });
          wordIndex++;
        }

        const clonedNode = node.cloneNode(true);
        wordWrapper.appendChild(clonedNode);
        node.parentNode.removeChild(node);
        return;
      }

      const children = Array.from(node.childNodes);
      children.forEach(child => processWordNodes(child, currentStyles));

      if (node.childNodes.length === 0 && node.tagName !== "BR") {
        node.parentNode.removeChild(node);
      }
    }
  };

  processWordNodes(tempDiv);

  // Include direction and transition in styleId so direction changes trigger new style tag
  const styleId = `word-rise-animation-${direction}-${transition}-${animationId}`;
  let existingStyle = document.getElementById(styleId);

  // Check if existing style has same word count - if content changed, we need to regenerate
  const existingWordCount = existingStyle?.dataset.wordCount;
  const wordCountMatches = existingWordCount === String(wordNodes.size);

  // When playing (not paused), reuse existing CSS to prevent animation restarts
  // Only recreate CSS when paused (for scrubbing) or first creation or content changed
  if (existingStyle && !isPaused && wordCountMatches) {
    return tempDiv.innerHTML;
  }

  // Clean up old rise animation styles with different directions/transitions
  cleanupOldAnimationStyles(
    "word-rise-animation",
    direction,
    transition,
    animationId
  );

  if (existingStyle) {
    existingStyle.remove();
  }

  const styleTag = document.createElement("style");
  styleTag.id = styleId;

  let cssRules = "";

  // Get start (progress=0) and end (progress=1) styles
  // getRiseStyles handles the direction differences between rise-in and rise-out
  const startStyles = getRiseStyles(direction, isRiseIn, 0, scaleX, scaleY);
  const endStyles = getRiseStyles(direction, isRiseIn, 1, scaleX, scaleY);

  wordNodes.forEach((data, wordWrapper) => {
    const wordClass = `.word-rise-${animationId}-${data.index}`;
    const wordDelay = isPreview
      ? data.index * CHARACTER_DELAY
      : delay + data.index * CHARACTER_DELAY;

    const keyframeName = `wordRise${direction}${transition}-${animationId}-${data.index}`;

    cssRules += `
      @keyframes ${keyframeName} {
        from {
          transform: ${startStyles.transform};
          clip-path: ${startStyles.clipPath};
        }
        to {
          transform: ${endStyles.transform};
          clip-path: ${endStyles.clipPath};
        }
      }
    `;

    let animationDelay;
    let animationPlayState = "";

    if (isPaused && !isPreview) {
      const wordStartTime = wordDelay;
      const relativeTime = relativeTimeToAnimation - wordStartTime;

      if (relativeTime < 0) {
        animationDelay = "0ms";
        animationPlayState = "paused";
      } else if (relativeTime >= ANIMATION_DURATION) {
        // Word animation has finished - show final state with compensation
        const compensatedDelay = -(ANIMATION_DURATION - RENDER_COMPENSATION_MS);
        animationDelay = `${compensatedDelay}ms`;
        animationPlayState = "paused";
      } else {
        // Word is mid-animation - scrub to exact position with compensation
        const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;
        animationDelay = `-${compensatedTime}ms`;
        animationPlayState = "paused";
      }
    } else {
      const wordStartTime = wordDelay;
      const relativeTime = relativeTimeToAnimation - wordStartTime;

      if (relativeTime < 0) {
        animationDelay = `${wordDelay}ms`;
        animationPlayState = "running";
      } else if (relativeTime >= ANIMATION_DURATION) {
        animationDelay = `-${ANIMATION_DURATION}ms`;
        animationPlayState = "paused";
      } else {
        animationDelay = `-${relativeTime}ms`;
        animationPlayState = "running";
      }
    }

    cssRules += `
      ${wordClass} {
        animation: ${keyframeName} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay} both ${animationPlayState};
        display: inline-block;
        white-space: inherit;
        position: relative;
        z-index: ${data.index + 1};
        will-change: transform, clip-path;
      }
      ${wordClass} * {
        display: inline-block;
        white-space: inherit;
      }
    `;
  });

  styleTag.textContent = cssRules;
  // Store word count to detect content changes
  styleTag.dataset.wordCount = String(wordNodes.size);
  document.head.appendChild(styleTag);

  return tempDiv.innerHTML;
};

/**
 * Creates character-level animation styles and processes displayValue
 * @param {Object} elementData - Element data containing animation config
 * @param {number} pageId - ID of the current page
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @returns {Object} Animation configuration with processed HTML
 */
const createCharacterAnimationStyles = (
  elementData,
  pageId,
  zoom,
  isPaused = false,
  currentPlayingTime = 0
) => {
  const {
    name,
    direction,
    transition,
    delay,
    originalDelay,
    isPreview,
    textType,
    randomOrder
  } = elementData.cssAnimation;
  const animationId = `text-anim-${elementData.uniqueId}`;

  if (!elementData.displayValue) {
    return null;
  }

  // Process the displayValue based on animation type
  const processedHTML =
    textType === "word"
      ? processWordAnimation(
          elementData.displayValue,
          animationId,
          { name, direction, transition, delay, originalDelay, isPreview },
          elementData,
          pageId,
          zoom,
          isPaused,
          currentPlayingTime
        )
      : processCharactersForAnimation(
          elementData.displayValue,
          animationId,
          {
            name,
            direction,
            transition,
            delay,
            originalDelay,
            isPreview,
            textType,
            randomOrder
          },
          elementData,
          pageId,
          zoom,
          isPaused,
          currentPlayingTime
        );

  return {
    processedDisplayValue: processedHTML,
    animationName: "none",
    animationDuration: "0ms"
  };
};

/**
 * Creates a custom slide animation for an element
 * @param {Object} elementData - Element data containing animation config
 * @param {string} animationName - Base animation name
 * @param {string} customAnimName - Custom animation name
 * @param {string} direction - Animation direction
 * @param {number} pageId - ID of the current page
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} relativeTime - Time relative to animation start
 * @returns {Object} Animation styles object
 */
const createSlideAnimation = (
  elementData,
  pageId,
  zoom,
  isPaused = false,
  relativeTime = 0
) => {
  const { name, direction, transition } = elementData.cssAnimation;
  const customAnimName = `custom${name}-${direction}-${transition}-${elementData.uniqueId}`;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  // When paused, calculate the exact position and apply transform directly
  // CSS animations don't reliably update when changing animation-delay mid-animation
  if (isPaused) {
    const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;

    // Calculate progress through animation (0 to 1)
    const progress = Math.min(
      1,
      Math.max(0, compensatedTime / ANIMATION_DURATION)
    );

    // Get the slide distance
    const CANVAS_PAGE_DOM = getCanvasPageElement(pageId);
    const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      width: 0,
      height: 0
    };
    const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
    const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
      width: elementData.width || 0,
      height: elementData.height || 0
    };

    const slideDistance = calculateSlideDistance(
      ELEMENT_BOUNDS,
      CANVAS_PAGE_BOUNDS,
      direction,
      zoom
    );

    // Flip compensation for paused animations
    const flipCompensationX = scaleX === -1 ? -1 : 1;
    const flipCompensationY = scaleY === -1 ? -1 : 1;

    // Calculate current position based on progress
    let currentTransform;
    let currentOpacity;

    if (transition === "in") {
      // Slide in: from offset to 0
      // Interpolate between start and end based on progress
      if (direction === "down") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateY(${-currentOffset *
          flipCompensationY}px)`;
      } else if (direction === "up") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateY(${currentOffset * flipCompensationY}px)`;
      } else if (direction === "left") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateX(${currentOffset * flipCompensationX}px)`;
      } else if (direction === "right") {
        const currentOffset = slideDistance * (1 - progress);
        currentTransform = `translateX(${-currentOffset *
          flipCompensationX}px)`;
      }

      currentOpacity = 1; // Slide-in animations are always visible
    } else {
      // Slide out: from 0 to offset
      if (direction === "down") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateY(${currentOffset * flipCompensationY}px)`;
      } else if (direction === "up") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateY(${-currentOffset *
          flipCompensationY}px)`;
      } else if (direction === "left") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateX(${-currentOffset *
          flipCompensationX}px)`;
      } else if (direction === "right") {
        const currentOffset = slideDistance * progress;
        currentTransform = `translateX(${currentOffset * flipCompensationX}px)`;
      }

      currentOpacity = 1 - progress;
    }

    return {
      position: "relative",
      transform: currentTransform,
      opacity: currentOpacity,
      visibility: "visible"
    };
  }

  const animationStyles = {
    // Use 0ms delay to let CSS animation run naturally from start
    // Never change this during playback to avoid restarts
    animationDelay: "0ms",
    animationDuration: `${ANIMATION_DURATION}ms`,
    animationName: customAnimName,
    animationIterationCount: 1,
    animationFillMode: "both",
    animationPlayState: "running"
  };

  if (transition === "in") {
    // For slide-in animations, element should be visible initially
    animationStyles.opacity = "1";
    animationStyles.visibility = "visible";
    // Use !important to ensure visibility overrides any other styles
    animationStyles.opacity = "1 !important";
  }

  // Before creating a new style tag, check if one with this ID already exists
  const customAnimationId = `custom-animation-${customAnimName}`;
  const existingStyleTag = document.getElementById(customAnimationId);

  // Don't recreate styles if they already exist (for both preview and playing modes)
  if (existingStyleTag) {
    return animationStyles;
  }

  // Clean up any old slide animations with different directions/transitions for this element
  cleanupOldCustomAnimationStyles(
    name,
    direction,
    transition,
    elementData.uniqueId
  );

  // Create new style tag since none exists
  const styleTag = document.createElement("style");
  styleTag.id = customAnimationId;

  let keyframeDefinition = "";

  const CANVAS_PAGE_DOM = getCanvasPageElement(pageId);
  const CANVAS_PAGE_BOUNDS = CANVAS_PAGE_DOM?.getBoundingClientRect() || {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: 0,
    height: 0
  };
  const ELEMENT_DOM = document.getElementById(elementData.uniqueId);
  const ELEMENT_BOUNDS = ELEMENT_DOM?.getBoundingClientRect() || {
    width: elementData.width || 0,
    height: elementData.height || 0
  };

  // Use shared utility to calculate slide distance
  const slideDistance = calculateSlideDistance(
    ELEMENT_BOUNDS,
    CANVAS_PAGE_BOUNDS,
    direction,
    zoom
  );

  // Define animations based on transition type (in or out)
  if (transition === "in") {
    keyframeDefinition = `
      @keyframes ${customAnimName} {
        0% {
          transform: ${getSlideTransform(
            slideDistance,
            direction,
            false,
            scaleX,
            scaleY
          )};
          opacity: 1;
        }
        100% {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }
      }
    `;
  } else if (transition === "out") {
    keyframeDefinition = `
      @keyframes ${customAnimName} {
        from {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }
        to {
          transform: ${getSlideTransform(
            slideDistance,
            direction,
            true,
            scaleX,
            scaleY
          )};
          opacity: 0;
        }
      }
    `;

    // For slide-out, ensure the element stays hidden after animation
    animationStyles.opacity = "0";
  }

  styleTag.textContent = keyframeDefinition;
  document.head.appendChild(styleTag);

  // Add an animation end listener to remove the style tag when animation completes
  const element = document.getElementById(elementData.uniqueId);
  if (element) {
    const onAnimationEnd = () => {
      const styleElement = document.getElementById(customAnimationId);
      if (styleElement) {
        styleElement.remove();
      }
      element.removeEventListener("animationend", onAnimationEnd);
    };

    element.addEventListener("animationend", onAnimationEnd);
  }

  return animationStyles;
};

/**
 * Creates a custom rise animation for an element using transform + clip-path.
 * Rise animations move the element while clipping it at its original boundary,
 * creating a "toast popping up from toaster" effect.
 * @param {Object} elementData - Element data containing animation config
 * @param {number} pageId - ID of the current page
 * @param {number} zoom - Current zoom level
 * @param {boolean} isPaused - Whether timeline is paused
 * @param {number} relativeTime - Time relative to animation start
 * @returns {Object} Animation styles object
 */
const createRiseAnimation = (
  elementData,
  pageId,
  zoom,
  isPaused = false,
  relativeTime = 0
) => {
  const { name, direction, transition } = elementData.cssAnimation;
  const customAnimName = `custom${name}-${direction}-${transition}-${elementData.uniqueId}`;

  // Extract flip state from element data (default to 1 if not flipped)
  const scaleX = elementData.scaleX ?? 1;
  const scaleY = elementData.scaleY ?? 1;

  const isRiseIn = transition === "in";

  // When paused, calculate the exact transform and clip-path and apply directly
  if (isPaused) {
    const RENDER_COMPENSATION_MS = 30;
    const compensatedTime = relativeTime + RENDER_COMPENSATION_MS;

    const progress = Math.min(
      1,
      Math.max(0, compensatedTime / ANIMATION_DURATION)
    );

    const riseStyles = getRiseStyles(
      direction,
      isRiseIn,
      progress,
      scaleX,
      scaleY
    );

    return {
      transform: riseStyles.transform,
      clipPath: riseStyles.clipPath,
      visibility: "visible"
    };
  }

  const animationStyles = {
    animationDelay: "0ms",
    animationDuration: `${ANIMATION_DURATION}ms`,
    animationName: customAnimName,
    animationIterationCount: 1,
    animationFillMode: "both",
    animationPlayState: "running"
  };

  if (transition === "in") {
    animationStyles.visibility = "visible";
  }

  // Before creating a new style tag, check if one already exists
  const customAnimationId = `custom-animation-${customAnimName}`;
  const existingStyleTag = document.getElementById(customAnimationId);

  if (existingStyleTag) {
    return animationStyles;
  }

  // Clean up old rise animations with different directions/transitions
  cleanupOldCustomAnimationStyles(
    name,
    direction,
    transition,
    elementData.uniqueId
  );

  // Create new style tag
  const styleTag = document.createElement("style");
  styleTag.id = customAnimationId;

  // Get start (progress=0) and end (progress=1) styles
  // getRiseStyles handles the direction differences between rise-in and rise-out
  const startStyles = getRiseStyles(direction, isRiseIn, 0, scaleX, scaleY);
  const endStyles = getRiseStyles(direction, isRiseIn, 1, scaleX, scaleY);

  const keyframeDefinition = `
    @keyframes ${customAnimName} {
      0% {
        transform: ${startStyles.transform};
        clip-path: ${startStyles.clipPath};
      }
      100% {
        transform: ${endStyles.transform};
        clip-path: ${endStyles.clipPath};
      }
    }
  `;

  styleTag.textContent = keyframeDefinition;
  document.head.appendChild(styleTag);

  // Add cleanup listener
  const element = document.getElementById(elementData.uniqueId);
  if (element) {
    const onAnimationEnd = () => {
      const styleElement = document.getElementById(customAnimationId);
      if (styleElement) {
        styleElement.remove();
      }
      element.removeEventListener("animationend", onAnimationEnd);
    };
    element.addEventListener("animationend", onAnimationEnd);
  }

  return animationStyles;
};

/**
 * Maps animation names to animate.css classes
 * @param {string} animationName - Animation name
 * @param {string} animationDirection - Animation direction
 * @param {string} animationTransition - Animation transition (in/out)
 * @param {boolean} isTextboxAnimation - Whether this is a textbox animation
 * @param {number} scaleX - Horizontal flip state (1 = normal, -1 = flipped)
 * @param {number} scaleY - Vertical flip state (1 = normal, -1 = flipped)
 * @returns {string} Animate.css class name
 */
const getAnimateCssClass = (
  animationName,
  animationDirection,
  animationTransition,
  isTextboxAnimation = false,
  scaleX = 1,
  scaleY = 1
) => {
  // Map our animation names to animate.css equivalents
  let name = animationName;
  if (animationName === "boing") {
    name = "bounce";
  }

  let direction = animationDirection;
  if (animationDirection === "horizontal") {
    direction = "x";
  } else if (animationDirection === "vertical") {
    direction = "y";
  } else if (animationDirection === "center") {
    direction = "";
    // library directions should be flipped for slide-in animations
  } else if (animationDirection === "left" && animationTransition === "in") {
    direction = "right";
  } else if (animationDirection === "right" && animationTransition === "in") {
    direction = "left";
  }

  // Compensate for element flipping:
  // When an element is flipped, animate.css animations are also affected by the flip transform
  // We need to swap the direction to maintain the intended visual animation
  if (
    name === "bounce" ||
    name === "back" ||
    name === "zoom" ||
    name === "fade"
  ) {
    // Handle vertical flip - swap up/down directions
    if (scaleY === -1) {
      if (direction === "up") {
        direction = "down";
      } else if (direction === "down") {
        direction = "up";
      }
    }

    // Handle horizontal flip - swap left/right directions
    if (scaleX === -1) {
      if (direction === "left") {
        direction = "right";
      } else if (direction === "right") {
        direction = "left";
      }
    }
  }

  return `${isTextboxAnimation ? "animate__" : ""}${name}${capitalize(
    animationTransition
  )}${capitalize(direction)}`;
};

/**
 * Converts cssAnimation object to inline style properties
 * @param {Object} cssAnimation - Animation configuration object
 * @param {number} cssAnimation.delay - Animation delay in milliseconds
 * @param {number} cssAnimation.duration - Animation duration in milliseconds
 * @param {string} cssAnimation.direction - Animation name/direction
 * @param {string} cssAnimation.transition - Animation transition
 * @param {boolean} isPlaying - Whether the animation is playing
 * @returns {Object|null} Animation style object or null if no animation
 */
export const getAnimationStyles = (
  elementData,
  isPlaying,
  pageId,
  zoom,
  currentPlayingTime = 0,
  isPaused = false
) => {
  if (!elementData?.cssAnimation) {
    return null;
  }

  // Handle multiple animations (new format)
  if (Array.isArray(elementData.cssAnimation)) {
    // When paused, treat it like playing so we can apply animation styles
    if (
      !isPlaying &&
      !isPaused &&
      !elementData.cssAnimation.some(anim => anim.isPreview)
    ) {
      // Even when not playing, check if element should be hidden due to start stage animation
      const startAnimation = elementData.cssAnimation.find(
        anim => anim.stage === ANIMATION_STAGES.START
      );

      if (startAnimation) {
        // Element has a start animation - should be hidden initially
        return {
          opacity: 0,
          visibility: "hidden"
        };
      }

      return null;
    }

    const activeAnimation = getActiveAnimationAtTime(
      elementData.cssAnimation,
      currentPlayingTime,
      elementData
    );

    if (!activeAnimation) {
      const startAnimation = elementData.cssAnimation.find(
        anim => anim.stage === ANIMATION_STAGES.START
      );

      if (startAnimation) {
        const startAnimationTime = startAnimation.delay || 0;

        if (currentPlayingTime < startAnimationTime) {
          // Element has a start animation that hasn't started yet - should be hidden
          return {
            opacity: 0,
            visibility: "hidden"
          };
        }
      }

      // SECOND: Check animate-out status
      const animateOutStatus = getAnimateOutStatus(
        elementData.cssAnimation,
        currentPlayingTime,
        elementData
      );

      if (animateOutStatus === "completed") {
        // Element has completed an animate out animation and should remain hidden
        return {
          opacity: 0,
          visibility: "hidden"
        };
      } else if (animateOutStatus === "waiting") {
        // Element is waiting for animate out to start, should be visible
        return {
          opacity: 1,
          visibility: "visible"
        };
      } else if (animateOutStatus === "active") {
        // Animation should be active but getActiveAnimationAtTime returned null
        // This is a fallback - find and process the active animate-out animation manually
        const endAnimation = elementData.cssAnimation.find(
          anim =>
            anim.stage === ANIMATION_STAGES.END && anim.transition === "out"
        );

        if (endAnimation) {
          const animationStartTime = endAnimation.delay || 0;
          const relativeTime = currentPlayingTime - animationStartTime;

          return processSingleAnimation(
            endAnimation,
            elementData,
            pageId,
            zoom,
            relativeTime,
            isPaused,
            currentPlayingTime
          );
        }
      }
      return {
        opacity: 1,
        visibility: "visible"
      };
    }

    // Clean up existing animation styles for multiple animations
    const animationId = `text-anim-${elementData.uniqueId}`;
    const charStyleId = `char-slide-animation-${activeAnimation.direction}-${activeAnimation.transition}-${activeAnimation.textType}-${animationId}`;
    const wordStyleId = `word-animation-${animationId}`;
    const customAnimName = `custom${activeAnimation.name}-${activeAnimation.direction}-${activeAnimation.transition}-${elementData.uniqueId}`;
    const customAnimationId = `custom-animation-${customAnimName}`;

    const isInPreviewMode = activeAnimation.isPreview;
    const hasExistingStyles =
      document.getElementById(charStyleId) ||
      document.getElementById(wordStyleId) ||
      document.getElementById(customAnimationId);

    // Don't clean up styles during active character/word/random animations
    // Character, word, and random animations manage their own lifecycle
    const isCharacterOrWordAnimation =
      activeAnimation.textType === "letter" ||
      activeAnimation.textType === "word" ||
      activeAnimation.textType === "random";

    // Only clean up styles if we're not in preview mode AND no existing styles are found AND not a character/word animation
    if (!isInPreviewMode && !hasExistingStyles && !isCharacterOrWordAnimation) {
      [charStyleId, wordStyleId, customAnimationId].forEach(styleId => {
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
          existingStyle.remove();
        }
      });
    }

    const styles = processSingleAnimation(
      activeAnimation,
      elementData,
      pageId,
      zoom,
      activeAnimation.relativeTime,
      isPaused,
      currentPlayingTime
    );

    return styles;
  }

  // Handle single animation (legacy format)
  // When paused, treat it like playing so we can apply animation styles
  if (!(isPlaying || isPaused || elementData.cssAnimation.isPreview)) {
    // Even when not playing, check if element should be hidden due to animate-in or start stage
    if (
      elementData.cssAnimation.transition === "in" ||
      elementData.cssAnimation.stage === ANIMATION_STAGES.START
    ) {
      // Element has an animate-in animation or start stage - should be hidden initially
      return {
        opacity: 0,
        visibility: "hidden"
      };
    }

    return null;
  }

  // For single animations, check if it's an animate-in or start stage that hasn't started yet
  if (
    (isPlaying || isPaused) &&
    (elementData.cssAnimation.transition === "in" ||
      elementData.cssAnimation.stage === ANIMATION_STAGES.START)
  ) {
    const animationStartTime = elementData.cssAnimation.delay || 0;

    if (currentPlayingTime < animationStartTime) {
      // Element has an animate-in animation or start stage that hasn't started yet - should be hidden
      return {
        opacity: 0,
        visibility: "hidden"
      };
    }
  }

  // Only clean up existing animation styles if we're not currently in preview mode
  // This prevents animation flickering during mouse movements while preview is active
  const animationId = `text-anim-${elementData.uniqueId}`;
  const charStyleId = `char-slide-animation-${elementData.cssAnimation.direction}-${elementData.cssAnimation.transition}-${elementData.cssAnimation.textType}-${animationId}`;
  const wordStyleId = `word-animation-${animationId}`;
  const customAnimName = `custom${elementData.cssAnimation.name}-${elementData.cssAnimation.direction}-${elementData.cssAnimation.transition}-${elementData.uniqueId}`;
  const customAnimationId = `custom-animation-${customAnimName}`;

  // Check if we're in preview mode and styles already exist
  const isInPreviewMode = elementData.cssAnimation.isPreview;
  const hasExistingStyles =
    document.getElementById(charStyleId) ||
    document.getElementById(wordStyleId) ||
    document.getElementById(customAnimationId);

  // Only clean up if not in preview mode OR if no existing styles are found
  if (!isInPreviewMode || !hasExistingStyles) {
    [charStyleId, wordStyleId, customAnimationId].forEach(styleId => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    });
  }

  return processSingleAnimation(
    elementData.cssAnimation,
    elementData,
    pageId,
    zoom,
    0,
    isPaused,
    currentPlayingTime
  );
};

/**
 * Combines element style with animation styles if present
 * @param {Object} elementStyle - Base element styles
 * @param {Object} elementData - Element data containing animation config
 * @param {boolean} isPlaying - Whether the animation is playing
 * @param {number} pageId - Page ID
 * @param {number} zoom - Zoom level
 * @param {number} currentPlayingTime - Current timeline time in milliseconds
 * @param {boolean} isPaused - Whether timeline is paused
 * @returns {Object} Combined style object
 */
export const getCombinedAnimatedStyles = (
  elementStyle,
  elementData,
  isPlaying,
  pageId,
  zoom,
  currentPlayingTime = 0,
  isPaused = false
) => {
  const animationStyles = getAnimationStyles(
    elementData,
    isPlaying,
    pageId,
    zoom,
    currentPlayingTime,
    isPaused
  );

  if (!animationStyles) {
    return elementStyle;
  }

  return {
    ...elementStyle,
    ...animationStyles
  };
};

export const shouldAnimateElement = (
  elementData,
  isPlaying,
  isPaused = false
) => {
  if (!elementData.cssAnimation) {
    return false;
  }

  // Handle multiple animations (array format)
  if (Array.isArray(elementData.cssAnimation)) {
    return Boolean(
      isPlaying ||
        isPaused ||
        elementData.cssAnimation.some(anim => anim.isPreview)
    );
  }

  // Handle single animation (legacy format)
  return Boolean(isPlaying || isPaused || elementData.cssAnimation?.isPreview);
};

/**
 * Calculates the total duration of an animation for all elements
 * @param {Array|Object} elements - Single element or array of elements being animated
 * @returns {number} Total animation duration in milliseconds
 */
export const calculateTotalAnimationDuration = elements => {
  // Ensure elements is an array
  const elementArray = Array.isArray(elements) ? elements : [elements];

  let maxDuration = 0;

  elementArray.forEach(element => {
    let elementDuration = 0;

    // Handle new cssAnimation array structure (multiple animations)
    if (element?.cssAnimation && Array.isArray(element.cssAnimation)) {
      const animations = element.cssAnimation;

      if (animations.length === 0) {
        elementDuration = 0;
      } else if (animations.length === 1) {
        // Single animation - calculate its duration
        const animation = animations[0];
        elementDuration = calculateSingleAnimationDuration(animation, element);
      } else {
        // Multiple animations - calculate from first start to last end based on stage
        // Find first and last animations by stage priority (start -> middle -> end)
        const stageOrder = [
          ANIMATION_STAGES.START,
          ANIMATION_STAGES.MIDDLE,
          ANIMATION_STAGES.END
        ];

        // Find the earliest stage animation (first to play)
        let firstAnimation = null;
        for (const stage of stageOrder) {
          firstAnimation = animations.find(anim => anim.stage === stage);
          if (firstAnimation) break;
        }

        // Find the latest stage animation (last to play)
        let lastAnimation = null;
        for (let i = stageOrder.length - 1; i >= 0; i--) {
          const stage = stageOrder[i];
          lastAnimation = animations.find(anim => anim.stage === stage);
          if (lastAnimation) break;
        }

        // Fallback to index-based sorting if stage-based approach fails
        if (!firstAnimation || !lastAnimation) {
          firstAnimation = animations[0];
          lastAnimation = animations[animations.length - 1];
        }

        const firstStartTime = firstAnimation.delay || 0;
        const lastStartTime = lastAnimation.delay || 0;
        const lastAnimationDuration = calculateSingleAnimationDuration(
          lastAnimation,
          element
        );

        // Total duration = (last animation start time + last animation duration) - first animation start time
        elementDuration =
          lastStartTime + lastAnimationDuration - firstStartTime;
      }
    }
    // Handle legacy cssAnimation object structure (single animation)
    else if (
      element?.cssAnimation &&
      typeof element.cssAnimation === "object"
    ) {
      elementDuration = calculateSingleAnimationDuration(
        element.cssAnimation,
        element
      );
    }
    // No animation
    else {
      elementDuration = 0;
    }

    maxDuration = Math.max(maxDuration, elementDuration);
  });

  return maxDuration;
};

/**
 * Calculates the duration of a single animation
 * @param {Object} animation - Single animation object
 * @param {Object} element - Element data for textbox duration calculations
 * @returns {number} Animation duration in milliseconds
 */
export const calculateSingleAnimationDuration = (animation, element) => {
  let animationDuration = ANIMATION_DURATION; // Base duration (1000ms)

  // For textbox elements, calculate additional duration based on text type
  // Note: element.type might be missing in some contexts, so also check if it's a text animation
  if (
    (element?.type === "textbox" ||
      (animation?.textType &&
        (animation.textType === "letter" ||
          animation.textType === "word" ||
          animation.textType === "random"))) &&
    animation?.textType
  ) {
    const textType = animation.textType;

    // If textType is "textbox", just use base duration
    if (textType === "textbox") {
      animationDuration = ANIMATION_DURATION;
    } else if (
      textType === "letter" ||
      textType === "word" ||
      textType === "random"
    ) {
      // Calculate character count for letter/word/random animations
      const displayValue = element.displayValue || "";
      const charCount = displayValue.replace(/<[^>]*>/g, "").length; // Strip HTML tags but keep spaces for counting

      if (charCount > 0) {
        const isTypeIn = animation?.name === "typeIn";

        if (isTypeIn) {
          // TypeIn uses natural, variable delays for realistic typing
          // Estimate total duration based on average delay + character fade duration
          const avgCharDelay = TYPEIN_BASE_DELAY; // ~65ms average between chars
          const perCharFadeDuration = 150; // Fade-in duration per character

          // Strip HTML and get just the text for accurate delay estimation
          const plainText = displayValue.replace(/<[^>]*>/g, "");
          const visibleChars = plainText.replace(/\s/g, "").length; // Only non-whitespace chars animate
          const spaceCount = (plainText.match(/ /g) || []).length;
          const punctCount = (plainText.match(/[.,!?;:-]/g) || []).length;

          // Estimate cumulative delay:
          // - Each visible char adds ~avgCharDelay
          // - Spaces add TYPEIN_SPACE_PAUSE (30ms)
          // - Punctuation adds TYPEIN_PUNCTUATION_PAUSE (150ms)
          const estimatedTotalDelay =
            (visibleChars - 1) * avgCharDelay + // -1 because first char has no delay
            spaceCount * TYPEIN_SPACE_PAUSE +
            punctCount * TYPEIN_PUNCTUATION_PAUSE;

          animationDuration = estimatedTotalDelay + perCharFadeDuration;
        } else {
          // Other animations (letter/word/random) use fixed delays
          // Calculate when the last character/word finishes animating
          // Last character/word delay + base animation duration = total duration
          // For random, duration is same as letter (delays are shuffled, not changed)
          const lastCharDelay =
            textType === "word"
              ? (Math.ceil(charCount / 5) - 1) * CHARACTER_DELAY // Approximate words by dividing chars by 5, subtract 1 because first word has 0 delay
              : (charCount - 1) * CHARACTER_DELAY; // Letter and random animations, subtract 1 because first letter has 0 delay

          animationDuration = lastCharDelay + ANIMATION_DURATION;
        }
      }
    }
  }

  return animationDuration;
};
